import { useEffect, useRef, useState } from 'react'
import { apiFetch, ApiError } from '../../../lib/api-client'
import { createSigningOrder, getSigningOrder, type SigningOrderResult, type SigningOrderStatus } from '../../../lib/signing'
import { toOere, type Lease, type Renter, type Company, type ErhvervContract, type TemplateSummary, type TemplateResponse, type ValidationError } from './types'
import { BLUE, SectionBlock, FieldRow, RadioPillList, Toggle, SubSectionDivider, inputStyle } from './ui'
import { WizardChrome, type StepStatus } from './WizardChrome'
import { ErhvervPreviewPanel } from './ErhvervPreviewPanel'
import { ErhvervSectionVilkaar, type LibraryClause } from './ErhvervSectionVilkaar'
import { punktTitle, punktText, vilkaarSectionNo, type ErhvervPunkt } from './erhvervVilkaar'

type ReguleringType = 'ingen' | 'indeks' | 'nettopris' | 'trappe' | 'fast_pct' | 'markedsleje'
interface TrappeStep { ikrafttraeden: string; aarlig_leje_oere: number; procent?: number }
interface IndexRow { indekstype: string; indeks_basis_period: string; procent: string }
// Erhverv leje line-item (B5). Beløb captured in kr.; converted to øre on send.
// A2: kategori ('leje' rent / 'bidrag' recurring faste bidrag) + gruppe
// (§§-opsplitning label; leje rows sharing a label collapse into one line).
interface LejeLineRow { gl_account: string; tekst: string; belob_dkk: string; momspligtig: boolean; kategori: 'leje' | 'bidrag'; gruppe: string }
// Andre engangsbeløb (C3) — one-off charge row. Beløb in kr.; → øre on send.
interface EngangsbeloebRow { bidragstype: string; tekst: string; belob_dkk: string; momspligtig: boolean }
// Opsplit indflytningsopkrævning (C6) — per-post row with its own forfaldsdato.
interface IndflytningPostRow { post: string; belob_dkk: string; forfaldsdato: string }
// C5 — live indflytningsopgørelse projection (read-only, computed backend-side).
interface IndflytningsopgoerelseLine { key: string; label: string; ekskl_oere: number; moms_oere: number; inkl_oere: number }
interface IndflytningsopgoerelseResult { lines: IndflytningsopgoerelseLine[]; totals: { ekskl_oere: number; moms_oere: number; inkl_oere: number } }
// Forbrug (A1) — per-forbrugstype afregning + aconto (kr.; → øre on send) + fuldmagt.
type Afregning = 'aconto_udlejer' | 'inkluderet' | 'mellem_lejer_forsyning' | 'intet'
interface ForbrugTypeRow { afregning: Afregning; aconto_dkk: string; fuldmagt: boolean }
type ForbrugType = 'el' | 'vand' | 'varme' | 'koeling'

const OPKRAEVNINGSINTERVAL_OPTIONS = [
  { value: 'maanedlig', label: 'Månedligt' },
  { value: 'kvartalsvis', label: 'Kvartalsvis' },
  { value: 'halvaarlig', label: 'Halvårligt' },
  { value: 'aarlig', label: 'Årligt' },
]

// Forbrug (A1) — afregning options per forbrugstype.
const AFREGNING_OPTIONS = [
  { value: 'aconto_udlejer', label: 'Aconto til udlejer' },
  { value: 'inkluderet', label: 'Inkluderet i lejen' },
  { value: 'mellem_lejer_forsyning', label: 'Mellem lejer og forsyningsselskab' },
  { value: 'intet', label: 'Intet forbrug' },
]
// Property-driven greying (competitor parity, COMPLETE-SPEC §2b): when a lejemål is
// NOT connected to a forbrugstype's forbrugsgruppe on the property, only "Mellem
// lejer og forsyningsselskab" and "Intet forbrug" are selectable; "Aconto til
// udlejer" and "Inkluderet i lejen" are greyed. These two require the lejemål to be
// tilkoblet the forbrugstype.
const FORBRUGSGRUPPE_GATED_AFREGNING: Afregning[] = ['aconto_udlejer', 'inkluderet']
const FORBRUG_UNAVAILABLE_NOTE =
  'Lejemålet er ikke tilkoblet forbrugstypen på ejendommen, og derfor kan du kun vælge Mellem lejer og forsyningsselskab og Intet forbrug'
// Actionable guidance (competitor parity): tell the user HOW to unlock the gated
// options, so the greying isn't a dead end.
const FORBRUG_UNLOCK_HINT =
  'Hvis lejer skal betale aconto forbrug, skal dette først opsættes på lejemålet ved at oprette forbrugsgruppen på ejendommen og derefter tilføje lejemålet til forbrugsgruppen.'
// Per-forbrugstype availability (is the lejemål tilkoblet this type's forbrugsgruppe?).
// Sourced from the lease detail's forbrug_availability map (GET /leases/{id},
// migr. 076) — a type is "available" only when the lejemål is a member of a
// forbrugsgruppe of that type on the property. Absent/empty → all-false, which
// correctly greys the gated radios for a lejemål not yet placed in a group.
type ForbrugAvailability = Record<ForbrugType, boolean>
const FORBRUG_TYPES: { key: ForbrugType; label: string }[] = [
  { key: 'el', label: 'el' },
  { key: 'vand', label: 'vand' },
  { key: 'varme', label: 'varme' },
  { key: 'koeling', label: 'køling' },
]
const FORBRUG_DEFAULT: ForbrugTypeRow = { afregning: 'mellem_lejer_forsyning', aconto_dkk: '', fuldmagt: false }

// Full multi-index catalog — mirrors backend core/erhverv_enums.INDEKS_TYPES.
const INDEKS_TYPE_OPTIONS = [
  { value: 'npi', label: 'Nettoprisindeks (NPI)' },
  { value: 'npi_aarsgennemsnit', label: 'Nettoprisindeks, årsgennemsnit' },
  { value: 'byg42_bolig', label: 'Byggeomkostningsindeks, boliger' },
  { value: 'byg42_enfamilie', label: 'Byggeomkostningsindeks, enfamiliehuse' },
  { value: 'byg42_etage', label: 'Byggeomkostningsindeks, etageboliger' },
  { value: 'ilon12', label: 'Implicit lønindeks (ILON12)' },
]
const REG_MONTHS = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December']

interface Props {
  lease: Lease
  renters: Renter[]
  company: Company | null
  // A3 — draft resume: an existing commercial contract to hydrate from, the
  // step to land on, and a callback fired when a draft is first persisted (so
  // the parent can push ?draft=<id> to the URL).
  initialContract?: ErhvervContract | null
  initialStep?: number
  onDraftCreated?: (contractId: string) => void
  onCancel: () => void
  onDone: (contractId: string) => void
}

// øre → kr. string for hydrating an amount input ('' when zero/absent).
const oereToInput = (o: number | null | undefined): string =>
  o == null || o === 0 ? '' : String(o / 100)

// A3 — map a stored forbrug-type blob back to a ForbrugTypeRow (defaults intact).
const VALID_AFREGNING: Afregning[] = ['aconto_udlejer', 'inkluderet', 'mellem_lejer_forsyning', 'intet']
const hydrateForbrug = (t?: { afregning?: string; aconto_oere?: number | null; fuldmagt?: boolean }): ForbrugTypeRow => ({
  afregning: t && VALID_AFREGNING.includes(t.afregning as Afregning) ? (t.afregning as Afregning) : FORBRUG_DEFAULT.afregning,
  aconto_dkk: oereToInput(t?.aconto_oere),
  fuldmagt: !!t?.fuldmagt,
})

const NAV_STEPS = ['Generelt', 'Leje', 'Forbrug', 'Betalinger', 'Regulering', 'Vilkår', 'Parter', 'Generering', 'Oversigt', 'Gennemse & send']
const USAGE_LABELS: Record<string, string> = { erhverv: 'Erhverv', kontor: 'Kontor', lager: 'Lager', andet: 'Andet' }

// Generering (A4-partial) — the OPTIONAL §-sections the user can toggle off in
// the Generering step. Keys mirror backend erhverv_pdf.OPTIONAL_SECTIONS exactly;
// the protected trio (Parterne, Det lejede, Leje) is never excludable and not
// listed here. `applies` decides whether a toggle is shown — a section only
// renders (and so only needs a toggle) when its underlying data is present.
type GenSectionKey =
  | 'anvendelse' | 'lejeperiode' | 'faste_bidrag' | 'forbrug' | 'depositum'
  | 'indflytningsopkraevning' | 'engangsbeloeb' | 'bankgaranti' | 'drift_skatter'
  | 'regulering' | 'vilkaar'
interface ExtraParagraf { title: string; content: string }

// Oversigt (A4-partial) — one read-only label/value row. Skips itself when the
// value is empty so the summary stays tight (only captured values show).
function OversigtRow({ label, value }: { label: string; value?: string | null }) {
  if (value == null || value === '') return null
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.1fr', gap: 12, padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ fontSize: 13, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 14, color: '#111827', whiteSpace: 'pre-wrap' }}>{value}</div>
    </div>
  )
}

const REG_OPTIONS = [
  { value: 'ingen', label: 'Ingen regulering' },
  { value: 'nettopris', label: 'Nettoprisindeksregulering' },
  { value: 'indeks', label: 'Indeksregulering (NPI / BYG43)' },
  { value: 'trappe', label: 'Trappeleje (aftalte trin)' },
  { value: 'fast_pct', label: 'Fast årlig procentregulering' },
  { value: 'markedsleje', label: 'Markedsleje (erhvervslejelovens § 13)' },
]

const Ico = (d: string) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: d }} />
)
const IcoHouse = Ico('<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/>')
const IcoCal = Ico('<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>')
const IcoCash = Ico('<rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>')
const IcoChart = Ico('<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>')
const IcoShield = Ico('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>')
const IcoDoc = Ico('<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>')
const IcoUser = Ico('<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>')
const IcoCheck = Ico('<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>')
const IcoBolt = Ico('<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>')

export function ErhvervWizardShell({ lease, renters, company, initialContract, initialStep, onDraftCreated, onCancel, onDone }: Props) {
  const [view, setView] = useState<'form' | 'preview'>('form')
  const [step, setStep] = useState(initialStep ?? 0)
  const [maxVisited, setMaxVisited] = useState(initialStep ?? 0)
  const [fetchKey, setFetchKey] = useState(0)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [thumbPages, setThumbPages] = useState(0)
  const [busy, setBusy] = useState(false)
  const [draftId, setDraftId] = useState<string | null>(initialContract?.id ?? null)
  // Idura MitID signing (step F). Once the order is created the wizard flips into
  // a read-only "sendt til underskrift" state; per-party status is polled from
  // GET /contracts/{id}/signing-order. sentResult holds the create response
  // (per-party hrefs); sentStatus holds the latest polled per-signatory status.
  const [sentResult, setSentResult] = useState<SigningOrderResult | null>(null)
  const [sentStatus, setSentStatus] = useState<SigningOrderStatus | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)

  // ── State ────────────────────────────────────────────────────────────────
  const [renterId, setRenterId] = useState('')
  const [formaal, setFormaal] = useState('')
  const [startDate, setStartDate] = useState('')
  const [tidsbegraenset, setTidsbegraenset] = useState(false)
  const [endDate, setEndDate] = useState('')
  // B3 — fixed-term lease: opsigeligt i perioden? (default uopsigeligt = false).
  const [tidsbegraensetOpsigelig, setTidsbegraensetOpsigelig] = useState(false)
  const [uopsigelig, setUopsigelig] = useState(false)
  // B2 — structured asymmetric uopsigelighed (lejer + udlejer period/unit).
  const [uopsigLejerVal, setUopsigLejerVal] = useState('')
  const [uopsigLejerEnhed, setUopsigLejerEnhed] = useState('aar')
  const [uopsigUdlejerVal, setUopsigUdlejerVal] = useState('')
  const [uopsigUdlejerEnhed, setUopsigUdlejerEnhed] = useState('aar')
  const [opsigelsesvarsel, setOpsigelsesvarsel] = useState('3')
  const [opsigelsesEnhed, setOpsigelsesEnhed] = useState('maaned')  // B1: dag|uge|maaned|aar
  const [afstaaelsesret, setAfstaaelsesret] = useState(false)
  // Opstart/Generelt (C2)
  const [istandsaettelse14, setIstandsaettelse14] = useState(false)
  const [planlaegSyn, setPlanlaegSyn] = useState(false)
  const [inspectionDate, setInspectionDate] = useState('')
  const [inspectionTime, setInspectionTime] = useState('')
  const [synEmailReminder, setSynEmailReminder] = useState(false)
  const [rentDkk, setRentDkk] = useState('')
  const [momspligtig, setMomspligtig] = useState(false)
  const [depositDkk, setDepositDkk] = useState('')
  const [prepaidDkk, setPrepaidDkk] = useState('')
  // Leje line-items (B5). Empty → monthly_rent_oere (rentDkk) is the source.
  const [lejeLines, setLejeLines] = useState<LejeLineRow[]>([])
  // Faste bidrag (A2) — leje opkrævningsinterval.
  const [opkraevningsinterval, setOpkraevningsinterval] = useState('maanedlig')
  // Betalinger (B5)
  const [bankgaranti, setBankgaranti] = useState(false)
  // C4-flag — Betalingsservice auto-tilmelding. Stored intent only; actual
  // enrolment is external Nets/PBS onboarding (no Betalingsservice/Nets call).
  const [betalingsserviceTilmeld, setBetalingsserviceTilmeld] = useState(false)
  // Bankgaranti registration sub-form (C2) — revealed when bankgaranti is on.
  // File upload deferred (no erhverv doc-storage infra yet).
  const [bgModtaget, setBgModtaget] = useState(false)
  const [bgForventetDato, setBgForventetDato] = useState('')
  const [bgBeloebKr, setBgBeloebKr] = useState('')
  const [bgStiller, setBgStiller] = useState('')
  const [bgStillerAdresse, setBgStillerAdresse] = useState('')
  const [bgStillerPostnr, setBgStillerPostnr] = useState('')
  const [bgStillerBy, setBgStillerBy] = useState('')
  const [bgMaaneder, setBgMaaneder] = useState('')
  const [bgModtagetDato, setBgModtagetDato] = useState('')
  const [bgGyldigTil, setBgGyldigTil] = useState('')
  const [bgNote, setBgNote] = useState('')
  // C2-upload — bankgaranti file (kopi af bankgarantien). Uploaded to the draft
  // contract; only the stored filename is held in wizard state.
  const [bgDocFilename, setBgDocFilename] = useState<string | null>(null)
  const [bgUploading, setBgUploading] = useState(false)
  const [bgUploadError, setBgUploadError] = useState<string | null>(null)
  // Depositum/forudbetalt (C1) — antal måneder as a dropdown. A numeric preset
  // (1/2/3) auto-computes the kr. amount = måneder × månedlig leje and disables
  // the amount input; 'andet' reveals a free måneder number + an editable amount;
  // 'ingen' = no måneder (amount entered manually / left blank).
  const [depositumMaaneder, setDepositumMaaneder] = useState('')
  const [forudbetaltMaaneder, setForudbetaltMaaneder] = useState('')
  const [depositumMode, setDepositumMode] = useState<'ingen' | '1' | '2' | '3' | 'andet'>('ingen')
  const [forudbetaltMode, setForudbetaltMode] = useState<'ingen' | '1' | '2' | '3' | 'andet'>('ingen')
  // Andre engangsbeløb (C3) — repeatable one-off charges. Beløb captured in kr.,
  // converted to øre on send. Per-row moms is REVISOR-GATED (computed downstream).
  const [engangsbeloeb, setEngangsbeloeb] = useState<EngangsbeloebRow[]>([])
  // Forbrug (A1) — per-forbrugstype afregning + aconto + fuldmagt, plus internet
  // and TV bidrag toggles (TV → tv_provider radio). Aconto in kr.; → øre on send.
  const [forbrugTyper, setForbrugTyper] = useState<Record<ForbrugType, ForbrugTypeRow>>({
    el: { ...FORBRUG_DEFAULT }, vand: { ...FORBRUG_DEFAULT },
    varme: { ...FORBRUG_DEFAULT }, koeling: { ...FORBRUG_DEFAULT },
  })
  // Per-forbrugstype availability — is the lejemål tilkoblet this type's forbrugsgruppe
  // on the property? Drives greying of the "Aconto til udlejer" / "Inkluderet i lejen"
  // radios (COMPLETE-SPEC §2b). Sourced from the lease detail's forbrug_availability
  // map (GET /leases/{id}, migr. 076); a type not in a group → gated radios disabled.
  const forbrugAvailability: ForbrugAvailability = {
    el: !!lease.forbrug_availability?.el,
    vand: !!lease.forbrug_availability?.vand,
    varme: !!lease.forbrug_availability?.varme,
    koeling: !!lease.forbrug_availability?.koeling,
  }
  const [internetBidrag, setInternetBidrag] = useState(false)
  const [internetAcontoDkk, setInternetAcontoDkk] = useState('')
  const [tvBidrag, setTvBidrag] = useState(false)
  const [tvAcontoDkk, setTvAcontoDkk] = useState('')
  const [tvProvider, setTvProvider] = useState<'udlejer' | 'antenneforening'>('udlejer')
  const [indflytningsopkraevning, setIndflytningsopkraevning] = useState(false)
  const [indflytningForfald, setIndflytningForfald] = useState('')
  // Opsplit indflytningsopkrævning (C6) — split the move-in invoice into posts,
  // each with its own forfaldsdato. Capture-only; the invoice is created at
  // activation. Beløb in kr.; → øre on send.
  const [indflytningOpsplit, setIndflytningOpsplit] = useState(false)
  const [indflytningPoster, setIndflytningPoster] = useState<IndflytningPostRow[]>([])
  const [reguleringType, setReguleringType] = useState<ReguleringType>('ingen')
  // Legacy single-index fields kept for back-compat; the multi-index rows below
  // supersede them in the UI. First index row carries the source going forward.
  const reguleringIndex: 'npi' | 'byg43' = 'npi'
  const reguleringBasis = ''
  const [reguleringPct, setReguleringPct] = useState('100')
  const [reguleringFirstDate, setReguleringFirstDate] = useState('')
  const [indexRows, setIndexRows] = useState<IndexRow[]>([])
  const [reguleringMaaned, setReguleringMaaned] = useState('Januar')
  const [reguleringFoersteAar, setReguleringFoersteAar] = useState('')
  const [reguleringMinPct, setReguleringMinPct] = useState('')
  const [reguleringMaxPct, setReguleringMaxPct] = useState('')
  // D4: regulerings-grænser (caps) — Procentvis (%) or Fast beløb (kr.).
  const [reguleringCapType, setReguleringCapType] = useState<'procent' | 'beloeb'>('procent')
  const [reguleringMinBeloeb, setReguleringMinBeloeb] = useState('')
  const [reguleringMaxBeloeb, setReguleringMaxBeloeb] = useState('')
  // D1: Nettoprisindeksregulering — single Indeksmåned + "mere end ét år" flag.
  const [reguleringIndeksMaaned, setReguleringIndeksMaaned] = useState('Oktober')
  const [reguleringIndeksMereEndAar, setReguleringIndeksMereEndAar] = useState(false)
  // D2: Fast årlig regulering — Procentvis (default) or a fixed kr. amount/year.
  const [reguleringFastType, setReguleringFastType] = useState<'procent' | 'beloeb'>('procent')
  const [reguleringFastBeloeb, setReguleringFastBeloeb] = useState('')
  // D5: "Skal lejer have en varslingsskrivelse?" — stored flag (default Fra).
  const [sendVarsling, setSendVarsling] = useState(false)
  // Auto-send toggle for varslingen (advokat-godkendt 2026-07-16, default Fra).
  const [sendVarslingAuto, setSendVarslingAuto] = useState(false)
  const [regulerDepositum, setRegulerDepositum] = useState(false)
  const [regulerForudbetalt, setRegulerForudbetalt] = useState(false)
  const [trappe, setTrappe] = useState<TrappeStep[]>([])
  // D3: trappe ladder is År→Beløb(kr.) (default) or År→Procent.
  const [trappeType, setTrappeType] = useState<'procent' | 'beloeb'>('beloeb')
  const [markedslejeDkk, setMarkedslejeDkk] = useState('')
  const [driftSkatter, setDriftSkatter] = useState('')
  const [library, setLibrary] = useState<LibraryClause[]>([])
  // Vilkår (punkt model) — ordered list of builtin/custom punkter, synced to
  // contract_clauses via PUT (atomic replace-all) on every draft save.
  const [vilkaarItems, setVilkaarItems] = useState<ErhvervPunkt[]>([])
  // Generering (A4-partial) — OPTIONAL §§ the user turned off + self-written §§.
  const [excludedSections, setExcludedSections] = useState<Set<GenSectionKey>>(new Set())
  const [extraParagraphs, setExtraParagraphs] = useState<ExtraParagraf[]>([])

  // Lejer-step nuances (C1) — additive flags on the selected renter, persisted
  // via PATCH /renters on send. Seeded from the renter when one is picked.
  const [manualAddress, setManualAddress] = useState(false)
  const [addressStreet, setAddressStreet] = useState('')
  const [addressPostnr, setAddressPostnr] = useState('')
  const [addressBy, setAddressBy] = useState('')
  const [addressLand, setAddressLand] = useState('')
  const [noEmail, setNoEmail] = useState(false)
  const [prefersLetter, setPrefersLetter] = useState(false)
  const [noCpr, setNoCpr] = useState(false)
  const [iban, setIban] = useState('')
  const [swift, setSwift] = useState('')
  // F5 — selskab e-faktura routing (EAN-nummer + faktura-email). Seeded from the
  // renter, persisted via PATCH /renters on send, rendered in §1 for selskaber.
  const [eanNumber, setEanNumber] = useState('')
  const [invoiceEmail, setInvoiceEmail] = useState('')

  // Extra parties (BATCH 1 item 3) — medlejere + kautionister beyond the
  // primary lejer. Each links an existing renter with a co_tenant/guarantor
  // role; posted to /contracts/{id}/parties on send (the PDF is role-aware).
  const [extraParties, setExtraParties] = useState<{ renter_id: string; role: 'co_tenant' | 'guarantor' }[]>([])

  // Flow templates (A3 part 2) — "Gem som skabelon" / "Brug skabelon".
  const [templates, setTemplates] = useState<TemplateSummary[]>([])
  const [templateName, setTemplateName] = useState('')
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templateMsg, setTemplateMsg] = useState<string | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')

  const loadTemplateList = () => {
    apiFetch<TemplateSummary[]>('/api/v1/erhverv-templates').then(setTemplates).catch(() => {})
  }
  useEffect(loadTemplateList, [])

  useEffect(() => { apiFetch<LibraryClause[]>('/api/v1/vilkaar/erhverv-library').then(setLibrary).catch(() => {}) }, [])

  // A3 — apply a contract field-set to the wizard state. Mirrors createBody()
  // in reverse: each field this wizard SENDs is mapped back. Used both to
  // hydrate a resumed draft (initialContract) and to stamp a saved template
  // ("Brug skabelon"). Parties are NOT part of this (identity is per-contract);
  // the draft-resume path fetches them separately below.
  const applyContractFields = (c: Partial<ErhvervContract>) => {
    setFormaal(c.erhverv_formaal ?? '')
    setStartDate(c.start_date ?? '')
    setTidsbegraenset(!!c.end_date)
    setEndDate(c.end_date ?? '')
    setTidsbegraensetOpsigelig(!!c.tidsbegraenset_opsigelig)
    setRentDkk(oereToInput(c.monthly_rent_oere))
    setMomspligtig(!!c.momspligtig)
    setDepositDkk(oereToInput(c.deposit_oere))
    setPrepaidDkk(oereToInput(c.prepaid_rent_oere))
    setDepositumMaaneder(c.depositum_maaneder != null ? String(c.depositum_maaneder) : '')
    setForudbetaltMaaneder(c.forudbetalt_maaneder != null ? String(c.forudbetalt_maaneder) : '')
    // A saved måneder count maps to a preset (1/2/3) else 'andet'; no count → 'ingen'.
    setDepositumMode(c.depositum_maaneder == null ? 'ingen' : ['1', '2', '3'].includes(String(c.depositum_maaneder)) ? (String(c.depositum_maaneder) as '1' | '2' | '3') : 'andet')
    setForudbetaltMode(c.forudbetalt_maaneder == null ? 'ingen' : ['1', '2', '3'].includes(String(c.forudbetalt_maaneder)) ? (String(c.forudbetalt_maaneder) as '1' | '2' | '3') : 'andet')

    setUopsigelig(c.uopsigelig_lejer_value != null || c.uopsigelig_udlejer_value != null)
    setUopsigLejerVal(c.uopsigelig_lejer_value != null ? String(c.uopsigelig_lejer_value) : '')
    setUopsigLejerEnhed(c.uopsigelig_lejer_enhed ?? 'aar')
    setUopsigUdlejerVal(c.uopsigelig_udlejer_value != null ? String(c.uopsigelig_udlejer_value) : '')
    setUopsigUdlejerEnhed(c.uopsigelig_udlejer_enhed ?? 'aar')
    setOpsigelsesvarsel(c.opsigelsesvarsel_maaneder != null ? String(c.opsigelsesvarsel_maaneder) : '3')
    setOpsigelsesEnhed(c.opsigelsesvarsel_enhed ?? 'maaned')
    setAfstaaelsesret(!!c.afstaaelsesret)
    setDriftSkatter(c.drift_skatter ?? '')
    // erhverv_saerlige_vilkaar is legacy: the resume path migrates it into a
    // custom vilkår punkt (and template apply does the same) — not state here.

    setIstandsaettelse14(!!c.istandsaettelse_14dage)
    setPlanlaegSyn(!!c.inspection_date)
    setInspectionDate(c.inspection_date ?? '')
    setInspectionTime(c.inspection_time ?? '')
    setSynEmailReminder(!!c.indflytningssyn_email_reminder)

    setOpkraevningsinterval(c.opkraevningsinterval ?? 'maanedlig')
    setLejeLines((c.leje_lines ?? []).map(l => ({
      gl_account: l.gl_account ?? '', tekst: l.tekst ?? '',
      belob_dkk: oereToInput(l.amount_oere), momspligtig: !!l.momspligtig,
      kategori: (l.kategori === 'bidrag' ? 'bidrag' : 'leje'), gruppe: l.gruppe ?? '',
    })))

    // Bankgaranti sub-form (C2 / C2-upload).
    setBankgaranti(!!c.bankgaranti)
    setBetalingsserviceTilmeld(!!c.betalingsservice_tilmeld)
    setBgModtaget(!!c.bankgaranti_modtaget)
    setBgForventetDato(c.bankgaranti_forventet_dato ?? '')
    setBgBeloebKr(oereToInput(c.bankgaranti_beloeb_oere))
    setBgStiller(c.bankgaranti_stiller ?? '')
    setBgStillerAdresse(c.bankgaranti_stiller_adresse ?? '')
    setBgStillerPostnr(c.bankgaranti_stiller_postnr ?? '')
    setBgStillerBy(c.bankgaranti_stiller_by ?? '')
    setBgMaaneder(c.bankgaranti_maaneder != null ? String(c.bankgaranti_maaneder) : '')
    setBgModtagetDato(c.bankgaranti_modtaget_dato ?? '')
    setBgGyldigTil(c.bankgaranti_gyldig_til ?? '')
    setBgNote(c.bankgaranti_note ?? '')
    setBgDocFilename(c.bankgaranti_document_filename ?? null)

    // Andre engangsbeløb (C3).
    setEngangsbeloeb((c.engangsbeloeb ?? []).map(e => ({
      bidragstype: e.bidragstype ?? '', tekst: e.tekst ?? '',
      belob_dkk: oereToInput(e.beloeb_oere), momspligtig: !!e.momspligtig,
    })))

    // Forbrug (A1).
    const f = c.forbrug ?? {}
    setForbrugTyper({
      el: hydrateForbrug(f.typer?.el), vand: hydrateForbrug(f.typer?.vand),
      varme: hydrateForbrug(f.typer?.varme), koeling: hydrateForbrug(f.typer?.koeling),
    })
    setInternetBidrag(!!f.internet?.bidrag)
    setInternetAcontoDkk(oereToInput(f.internet?.aconto_oere))
    setTvBidrag(!!f.tv?.bidrag)
    setTvAcontoDkk(oereToInput(f.tv?.aconto_oere))
    setTvProvider(f.tv?.tv_provider === 'antenneforening' ? 'antenneforening' : 'udlejer')

    // Indflytningsopkrævning (+ C6 opsplit).
    setIndflytningsopkraevning(!!c.indflytningsopkraevning)
    setIndflytningForfald(c.indflytning_forfald ?? '')
    setIndflytningOpsplit(!!c.indflytning_opsplit)
    setIndflytningPoster((c.indflytning_poster ?? []).map(p => ({
      post: p.post ?? '', belob_dkk: oereToInput(p.beloeb_oere), forfaldsdato: p.forfaldsdato ?? '',
    })))

    // Regulering.
    setReguleringType((c.regulering_type as ReguleringType) ?? 'ingen')
    setReguleringPct(c.regulering_pct != null ? String(c.regulering_pct) : '100')
    setReguleringFirstDate(c.regulering_first_date ?? '')
    setIndexRows((c.regulering_indices ?? []).map(r => ({
      indekstype: r.indekstype ?? '', indeks_basis_period: r.indeks_basis_period ?? '',
      procent: r.procent != null ? String(r.procent) : '100',
    })))
    setReguleringMaaned(c.regulering_maaned ?? 'Januar')
    setReguleringFoersteAar(c.regulering_foerste_aar != null ? String(c.regulering_foerste_aar) : '')
    setReguleringMinPct(c.regulering_min_pct != null ? String(c.regulering_min_pct) : '')
    setReguleringMaxPct(c.regulering_max_pct != null ? String(c.regulering_max_pct) : '')
    setReguleringCapType(c.regulering_cap_type === 'beloeb' ? 'beloeb' : 'procent')
    setReguleringMinBeloeb(oereToInput(c.regulering_min_beloeb_oere))
    setReguleringMaxBeloeb(oereToInput(c.regulering_max_beloeb_oere))
    setReguleringIndeksMaaned(c.regulering_indeks_maaned ?? 'Oktober')
    setReguleringIndeksMereEndAar(!!c.regulering_indeks_mere_end_aar)
    setReguleringFastType(c.regulering_fast_type === 'beloeb' ? 'beloeb' : 'procent')
    setReguleringFastBeloeb(oereToInput(c.regulering_fast_beloeb_oere))
    setSendVarsling(!!c.send_varsling)
    setSendVarslingAuto(!!c.send_varsling_auto)
    setRegulerDepositum(!!c.reguler_depositum)
    setRegulerForudbetalt(!!c.reguler_forudbetalt)
    setTrappe((c.regulering_trappe ?? []).map(s => ({
      ikrafttraeden: s.ikrafttraeden ?? '', aarlig_leje_oere: s.aarlig_leje_oere ?? 0, procent: s.procent,
    })))
    setTrappeType(c.regulering_trappe_type === 'procent' ? 'procent' : 'beloeb')
    setMarkedslejeDkk(oereToInput(c.markedsleje_target_oere))

    // Generering (A4-partial).
    setExcludedSections(new Set((c.generering?.excluded_sections ?? []) as GenSectionKey[]))
    setExtraParagraphs((c.generering?.extra_paragraphs ?? []).map(p => ({ title: p.title ?? '', content: p.content ?? '' })))
  }

  // A3 — hydrate the wizard from an existing draft, once, on mount. The renter
  // (primary tenant) + extra parties live in contract_parties, so they're
  // fetched separately. A guard keeps it from re-running and clobbering edits.
  // clausesHydrated guards syncClauses: we must not PUT an empty list before the
  // resume GET has resolved. For NEW contracts (no initialContract) there is
  // nothing to lose, so it starts true. For resumed drafts it is set true only
  // once the clauses GET succeeds; on a failed GET we leave it false so every
  // syncClauses call is a no-op until the user triggers a manual save (by which
  // point the component has re-mounted or the user has consciously acted).
  const clausesHydrated = useRef(!initialContract)
  // Reactive mirror for the Vilkår-step warning banner when the resume GET
  // failed and clause saving is therefore blocked (see syncClauses guard).
  const [clausesSyncBlocked, setClausesSyncBlocked] = useState(false)
  const hydratedRef = useRef(false)
  useEffect(() => {
    const c = initialContract
    if (!c || hydratedRef.current) return
    hydratedRef.current = true
    applyContractFields(c)
    // Vilkår punkter: the stored contract_clauses are the source of truth. The
    // library is fetched HERE (not awaited from the standalone effect) so the
    // builtin-matching below can never race an empty library. Exact title match
    // against a builtin row → builtin punkt (clean when content equals, override
    // when edited); no match → custom punkt. The legacy free-text field is
    // migrated in as a final custom punkt.
    // NOTE: clauses GET uses null sentinel on failure so we can distinguish
    // "empty list" from "fetch failed" — on failure we skip hydration entirely
    // and leave clausesHydrated false so autosave cannot wipe stored clauses.
    Promise.all([
      apiFetch<{ paragraph_number: string; content: string }[]>(`/api/v1/contracts/${c.id}/clauses`)
        .catch(() => null as null),
      apiFetch<LibraryClause[]>('/api/v1/vilkaar/erhverv-library')
        .catch(() => [] as LibraryClause[]),
    ]).then(([rows, lib]) => {
      if (rows === null) {
        // Clauses GET failed transiently — leave clausesHydrated false so
        // syncClauses is blocked until a successful explicit save, and show
        // the warning banner on the Vilkår step so the block isn't silent.
        setClausesSyncBlocked(true)
        return
      }
      clausesHydrated.current = true
      setClausesSyncBlocked(false)
      const punkter: ErhvervPunkt[] = rows.map((r, i) => {
        const b = lib.find(x => x.source === 'builtin' && x.title === r.paragraph_number)
        if (b && b.key) {
          return r.content === b.content
            ? { kind: 'builtin', key: b.key, title: b.title ?? 'Vilkår', baseText: b.content }
            : { kind: 'builtin', key: b.key, title: b.title ?? 'Vilkår', baseText: b.content, overrideText: r.content }
        }
        return { kind: 'custom', localId: `r-${i}`, text: r.content }
      })
      if (c.erhverv_saerlige_vilkaar?.trim()) punkter.push({ kind: 'custom', localId: 'legacy-sv', text: c.erhverv_saerlige_vilkaar })
      setVilkaarItems(punkter)
    })
    apiFetch<{ renter_id: string; role: string }[]>(`/api/v1/contracts/${c.id}/parties`)
      .then(parties => {
        const primary = parties.find(p => p.role === 'primary_tenant')
        if (primary) setRenterId(primary.renter_id)
        setExtraParties(parties
          .filter(p => p.role === 'co_tenant' || p.role === 'guarantor')
          .map(p => ({ renter_id: p.renter_id, role: p.role as 'co_tenant' | 'guarantor' })))
      })
      .catch(() => { /* no parties yet on an early draft */ })
  }, [initialContract])

  const selectedRenter = renters.find(r => r.id === renterId)
  // Seed the nuance toggles from the picked renter (one-shot on selection).
  // Intentional one-shot seed of local edit-state from the newly selected
  // renter; moving this to render-time derived state would change the timing
  // of when these become user-editable overrides vs. re-derived each render.
  useEffect(() => {
    if (!selectedRenter) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setManualAddress(!!selectedRenter.manual_address)
    setAddressStreet(selectedRenter.address_street ?? '')
    setAddressPostnr(selectedRenter.address_postnr ?? '')
    setAddressBy(selectedRenter.address_by ?? '')
    setAddressLand(selectedRenter.address_land ?? '')
    setNoEmail(!!selectedRenter.no_email)
    setPrefersLetter(!!selectedRenter.prefers_letter)
    setNoCpr(!!selectedRenter.no_cpr)
    setIban(selectedRenter.iban ?? '')
    setSwift(selectedRenter.swift ?? '')
    setEanNumber(selectedRenter.ean_number ?? '')
    setInvoiceEmail(selectedRenter.invoice_email ?? '')
  }, [renterId]) // eslint-disable-line react-hooks/exhaustive-deps

  // C1 — monthly leje basis for the depositum/forudbetalt presets: the sum of
  // kategori='leje' line-items if any carry an amount, else the single månedlig
  // leje (rentDkk). Money stays integer øre.
  const monthlyLejeOere = (() => {
    const lines = lejeLines.filter(l => l.kategori !== 'bidrag').reduce((s, l) => s + toOere(l.belob_dkk), 0)
    return lines > 0 ? lines : toOere(rentDkk)
  })()
  const oereToKr = (o: number) => (o / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, useGrouping: false }).replace('.', ',')

  // C1 — a numeric depositum preset auto-computes måneder + the kr. amount.
  // Syncs the editable måneder/kr. fields from the preset dropdown;
  // deliberately an effect (not derived-at-render) since the user can still
  // hand-edit the kr. amount afterwards without it being clobbered every render.
  useEffect(() => {
    if (depositumMode === 'ingen' || depositumMode === 'andet') return
    const m = parseInt(depositumMode)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDepositumMaaneder(String(m))
    setDepositDkk(oereToKr(m * monthlyLejeOere))
  }, [depositumMode, monthlyLejeOere])
  // Same pattern as the depositum preset effect above.
  useEffect(() => {
    if (forudbetaltMode === 'ingen' || forudbetaltMode === 'andet') return
    const m = parseInt(forudbetaltMode)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setForudbetaltMaaneder(String(m))
    setPrepaidDkk(oereToKr(m * monthlyLejeOere))
  }, [forudbetaltMode, monthlyLejeOere])

  // C5 — live indflytningsopgørelse projection (read-only). Leje for perioden
  // (prorated to month end) + øvrige faste bidrag + depositum + forudbetalt,
  // with ekskl./moms/inkl. totals. Computed by the backend so the panel can
  // never disagree with the move-in invoice (moms is REVISOR-GATED there).
  const oevrigeBidragOere = lejeLines.filter(l => l.kategori === 'bidrag').reduce((s, l) => s + toOere(l.belob_dkk), 0)
  const [indflytningsopgoerelse, setIndflytningsopgoerelse] = useState<IndflytningsopgoerelseResult | null>(null)
  // Debounced fetch of a backend-computed preview; the early-return branch
  // resets the preview to null before the (later, unconditional) debounced
  // fetch below runs.
  useEffect(() => {
    const depo = toOere(depositDkk)
    const pre = toOere(prepaidDkk)
    if (!startDate && monthlyLejeOere === 0 && depo === 0 && pre === 0 && oevrigeBidragOere === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIndflytningsopgoerelse(null)
      return
    }
    let cancelled = false
    const t = setTimeout(() => {
      apiFetch<IndflytningsopgoerelseResult>('/api/v1/contracts/erhverv-indflytningsopgoerelse-preview', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          begyndelsesdato: startDate || null, monthly_rent_oere: monthlyLejeOere,
          deposit_oere: depo, prepaid_rent_oere: pre, oevrige_oere: oevrigeBidragOere,
          usage_type: 'erhverv', momspligtig,
        }),
      }).then(r => { if (!cancelled) setIndflytningsopgoerelse(r) }).catch(() => { if (!cancelled) setIndflytningsopgoerelse(null) })
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [startDate, monthlyLejeOere, depositDkk, prepaidDkk, oevrigeBidragOere, momspligtig])

  const lejemaal = [lease.property_address, lease.unit_no].filter(Boolean).join(', ')

  // Kontoplan options for the Leje line "Konto" dropdown — the per-company
  // gl_accounts (migr. 043). Empty → konto is free-text (no dropdown options).
  const kontoOptions = Object.entries(company?.gl_accounts ?? {}).map(([value, label]) => ({ value, label: `${value} · ${label}` }))

  // ── Generering (A4-partial) — which OPTIONAL §§ currently apply ────────────
  // A section gets a toggle only when its underlying data is present (i.e. it
  // would render in the PDF) — mirrors the conditions in erhverv_pdf.py. The
  // protected trio (Parterne, Det lejede, Leje) is never listed (not excludable).
  const hasBidragRows = lejeLines.some(l => l.kategori === 'bidrag' && toOere(l.belob_dkk) > 0)
  const hasForbrug = (
    FORBRUG_TYPES.some(({ key }) => forbrugTyper[key].afregning) || internetBidrag || tvBidrag
  )
  const genSectionApplies = (key: GenSectionKey): boolean => {
    switch (key) {
      case 'anvendelse': return true
      case 'lejeperiode': return true
      case 'faste_bidrag': return hasBidragRows
      case 'forbrug': return hasForbrug
      case 'depositum': return true
      case 'indflytningsopkraevning': return indflytningsopkraevning
      case 'engangsbeloeb': return engangsbeloeb.some(e => toOere(e.belob_dkk) > 0)
      case 'bankgaranti': return bankgaranti
      case 'drift_skatter': return !!driftSkatter.trim()
      case 'regulering': return reguleringType !== 'ingen'
      case 'vilkaar': return vilkaarItems.length > 0
    }
  }
  // Label + one-line description per OPTIONAL section, in PDF render order.
  const GEN_SECTIONS: { key: GenSectionKey; label: string; hint: string }[] = [
    { key: 'anvendelse', label: 'Anvendelse og formål', hint: 'Det aftalte erhvervsformål' },
    { key: 'lejeperiode', label: 'Lejeperiode og uopsigelighed', hint: 'Begyndelse, ophør og uopsigelighed' },
    { key: 'faste_bidrag', label: 'Faste bidrag', hint: 'Tilbagevendende bidrag ud over lejen' },
    { key: 'forbrug', label: 'Forbrug', hint: 'Afregning af el, vand, varme og køling' },
    { key: 'depositum', label: 'Depositum og forudbetalt leje', hint: 'Sikkerhedsstillelse ved begyndelse' },
    { key: 'indflytningsopkraevning', label: 'Indflytningsopkrævning', hint: 'Opkrævning ved indflytning' },
    { key: 'engangsbeloeb', label: 'Engangsbeløb', hint: 'Engangsbeløb ud over leje og depositum' },
    { key: 'bankgaranti', label: 'Bankgaranti', hint: 'Registreret bankgaranti' },
    { key: 'drift_skatter', label: 'Drift og skatter', hint: 'Aftalt fordeling af driftsudgifter' },
    { key: 'regulering', label: 'Regulering af leje', hint: 'Hvordan lejen reguleres over tid' },
    { key: 'vilkaar', label: 'Vilkår', hint: 'Kontraktens vilkårspunkter' },
  ]
  const toggleExcluded = (key: GenSectionKey) => setExcludedSections(prev => {
    const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n
  })

  // §-number of the Vilkår section — mirrors erhverv_pdf.py's section order so
  // the punkt numbering shown in the wizard (vilkaarNo.1, vilkaarNo.2, …)
  // matches the rendered contract. Every input maps to existing wizard state;
  // `included` reflects the Generering exclude-set (true for non-excluded keys).
  const vilkaarNo = vilkaarSectionNo({
    included: k => !excludedSections.has(k as GenSectionKey),
    hasBidragRows,
    forbrugOn: hasForbrug,  // mirrors the PDF's (has_type or has_bidrag) test
    indflytningsopkraevning,
    hasEngangsbeloeb: engangsbeloeb.some(e => toOere(e.belob_dkk) > 0),
    hasBankgaranti: bankgaranti,
    hasDriftSkatter: !!driftSkatter.trim(),
    reguleringOn: reguleringType !== 'ingen',
  })

  // ── Bodies ───────────────────────────────────────────────────────────────
  const createBody = () => ({
    lease_id: lease.id, type: 'commercial',
    last_step: step,  // A3 — resume lands on the last-visited step
    monthly_rent_oere: toOere(rentDkk), deposit_oere: toOere(depositDkk), prepaid_rent_oere: toOere(prepaidDkk),
    start_date: startDate || null, end_date: tidsbegraenset ? (endDate || null) : null, momspligtig,
    // B3 — opsigelig i perioden only meaningful for a tidsbegrænset lease.
    tidsbegraenset_opsigelig: tidsbegraenset ? tidsbegraensetOpsigelig : null,
    erhverv_formaal: formaal || null, erhverv_uopsigelighed: null,
    uopsigelig_lejer_value: uopsigelig && uopsigLejerVal ? parseInt(uopsigLejerVal) : null,
    uopsigelig_lejer_enhed: uopsigelig && uopsigLejerVal ? uopsigLejerEnhed : null,
    uopsigelig_udlejer_value: uopsigelig && uopsigUdlejerVal ? parseInt(uopsigUdlejerVal) : null,
    uopsigelig_udlejer_enhed: uopsigelig && uopsigUdlejerVal ? uopsigUdlejerEnhed : null,
    // Legacy free-text field — nulled once the punkt list is hydrated (on resume
    // it migrates into a custom vilkår punkt; leaving it set would double-render
    // in the PDF). Until hydration has succeeded the key is omitted entirely
    // (undefined → dropped by JSON.stringify; PATCH uses exclude_unset) so a
    // failed resume GET can't null the legacy text before it was migrated.
    erhverv_saerlige_vilkaar: clausesHydrated.current ? null : undefined,
    opsigelsesvarsel_maaneder: opsigelsesvarsel ? parseInt(opsigelsesvarsel) : null,
    opsigelsesvarsel_enhed: opsigelsesEnhed,
    afstaaelsesret, drift_skatter: driftSkatter || null,
    // Opstart/Generelt (C2)
    istandsaettelse_14dage: istandsaettelse14,
    inspection_date: planlaegSyn ? (inspectionDate || null) : null,
    inspection_time: planlaegSyn ? (inspectionTime || null) : null,
    indflytningssyn_email_reminder: planlaegSyn ? synEmailReminder : false,
    regulering_type: reguleringType,
    regulering_index: reguleringType === 'indeks' ? reguleringIndex : null,
    regulering_basis_period: reguleringBasis || null,
    regulering_pct: reguleringPct ? parseFloat(reguleringPct) : null,
    regulering_first_date: reguleringFirstDate || null,
    regulering_trappe: trappe,
    regulering_trappe_type: reguleringType === 'trappe' ? trappeType : 'beloeb',
    markedsleje_target_oere: markedslejeDkk ? toOere(markedslejeDkk) : null,
    // Multi-index regulering (A6) — only sent for the indeks type.
    regulering_indices: reguleringType === 'indeks'
      ? indexRows.filter(r => r.indekstype).map(r => ({
          indekstype: r.indekstype,
          indeks_basis_period: r.indeks_basis_period || null,
          procent: r.procent ? parseFloat(r.procent) : 100,
        }))
      : [],
    regulering_maaned: (reguleringType === 'indeks' || reguleringType === 'nettopris') ? reguleringMaaned || null : null,
    regulering_foerste_aar: (reguleringType === 'indeks' || reguleringType === 'nettopris') && reguleringFoersteAar ? parseInt(reguleringFoersteAar) : null,
    regulering_min_pct: reguleringMinPct ? parseFloat(reguleringMinPct) : null,
    regulering_max_pct: reguleringMaxPct ? parseFloat(reguleringMaxPct) : null,
    // D4: regulerings-grænser type + kr.-amount caps (only when type='beloeb').
    regulering_cap_type: reguleringCapType,
    regulering_min_beloeb_oere: reguleringCapType === 'beloeb' && reguleringMinBeloeb ? toOere(reguleringMinBeloeb) : null,
    regulering_max_beloeb_oere: reguleringCapType === 'beloeb' && reguleringMaxBeloeb ? toOere(reguleringMaxBeloeb) : null,
    // D1: Nettoprisindeksregulering inputs — only sent for the nettopris type.
    regulering_indeks_maaned: reguleringType === 'nettopris' ? reguleringIndeksMaaned || null : null,
    regulering_indeks_mere_end_aar: reguleringType === 'nettopris' ? reguleringIndeksMereEndAar : false,
    // D2: Fast årlig regulering — Procentvis or a fixed kr. amount.
    regulering_fast_type: reguleringType === 'fast_pct' ? reguleringFastType : 'procent',
    regulering_fast_beloeb_oere: reguleringType === 'fast_pct' && reguleringFastType === 'beloeb' && reguleringFastBeloeb ? toOere(reguleringFastBeloeb) : null,
    // D5: varslingsskrivelse flag — only meaningful when regulering is active.
    send_varsling: reguleringType !== 'ingen' ? sendVarsling : false,
    // Auto-send only meaningful when varslingsskrivelsen itself is enabled.
    send_varsling_auto: reguleringType !== 'ingen' && sendVarsling ? sendVarslingAuto : false,
    reguler_depositum: regulerDepositum,
    reguler_forudbetalt: regulerForudbetalt,
    // Leje line-items (B5) — only rows with an amount are sent. Empty → the
    // single monthly_rent_oere above is the back-compat source of truth.
    leje_lines: lejeLines
      .filter(l => toOere(l.belob_dkk) > 0)
      .map((l, i) => ({ gl_account: l.gl_account || null, tekst: l.tekst || null, amount_oere: toOere(l.belob_dkk), momspligtig: l.momspligtig, sort: i, kategori: l.kategori, gruppe: l.gruppe || null })),
    // Faste bidrag (A2) — leje opkrævningsinterval.
    opkraevningsinterval: opkraevningsinterval,
    // Betalinger (B5)
    bankgaranti,
    // C4-flag — Betalingsservice tilmelding (stored intent; no Nets call).
    betalingsservice_tilmeld: betalingsserviceTilmeld,
    // Bankgaranti sub-form (C2) — only meaningful when bankgaranti is on.
    bankgaranti_modtaget: bankgaranti ? bgModtaget : false,
    bankgaranti_forventet_dato: bankgaranti && !bgModtaget ? (bgForventetDato || null) : null,
    bankgaranti_beloeb_oere: bankgaranti && bgModtaget && bgBeloebKr ? toOere(bgBeloebKr) : null,
    bankgaranti_stiller: bankgaranti && bgModtaget ? (bgStiller || null) : null,
    bankgaranti_stiller_adresse: bankgaranti && bgModtaget ? (bgStillerAdresse || null) : null,
    bankgaranti_stiller_postnr: bankgaranti && bgModtaget ? (bgStillerPostnr || null) : null,
    bankgaranti_stiller_by: bankgaranti && bgModtaget ? (bgStillerBy || null) : null,
    bankgaranti_maaneder: bankgaranti && bgModtaget && bgMaaneder ? parseInt(bgMaaneder) : null,
    bankgaranti_modtaget_dato: bankgaranti && bgModtaget ? (bgModtagetDato || null) : null,
    bankgaranti_gyldig_til: bankgaranti && bgModtaget ? (bgGyldigTil || null) : null,
    bankgaranti_note: bankgaranti && bgModtaget ? (bgNote || null) : null,
    depositum_maaneder: depositumMaaneder ? parseInt(depositumMaaneder) : null,
    forudbetalt_maaneder: forudbetaltMaaneder ? parseInt(forudbetaltMaaneder) : null,
    // Andre engangsbeløb (C3) — only rows with an amount are sent. Beløb → øre.
    engangsbeloeb: engangsbeloeb
      .filter(e => toOere(e.belob_dkk) > 0)
      .map(e => ({ bidragstype: e.bidragstype || null, tekst: e.tekst || null, beloeb_oere: toOere(e.belob_dkk), momspligtig: e.momspligtig })),
    // Forbrug (A1) — per-type afregning + aconto (øre) + fuldmagt, internet/TV
    // bidrag + tv_provider. Aconto stored in the blob (rendered in §Forbrug), not
    // mutated onto leje_lines — see CommercialContractModel.forbrug for the why.
    forbrug: {
      typer: Object.fromEntries(FORBRUG_TYPES.map(({ key }) => {
        const t = forbrugTyper[key]
        return [key, {
          afregning: t.afregning,
          aconto_oere: t.afregning === 'aconto_udlejer' && toOere(t.aconto_dkk) > 0 ? toOere(t.aconto_dkk) : null,
          fuldmagt: t.fuldmagt,
        }]
      })),
      internet: { bidrag: internetBidrag, aconto_oere: internetBidrag && toOere(internetAcontoDkk) > 0 ? toOere(internetAcontoDkk) : null },
      tv: { bidrag: tvBidrag, aconto_oere: tvBidrag && toOere(tvAcontoDkk) > 0 ? toOere(tvAcontoDkk) : null, tv_provider: tvBidrag ? tvProvider : null },
    },
    indflytningsopkraevning,
    indflytning_forfald: indflytningForfald || null,
    // Opsplit indflytningsopkrævning (C6) — only when both the move-in invoice
    // and the opsplit toggle are on. Beløb → øre.
    indflytning_opsplit: indflytningsopkraevning ? indflytningOpsplit : false,
    indflytning_poster: indflytningsopkraevning && indflytningOpsplit
      ? indflytningPoster
          .filter(p => p.post || toOere(p.belob_dkk) > 0 || p.forfaldsdato)
          .map(p => ({ post: p.post || null, beloeb_oere: toOere(p.belob_dkk), forfaldsdato: p.forfaldsdato || null }))
      : [],
    // Generering (A4-partial) — excluded OPTIONAL §§ + self-written paragraffer.
    // Only sections currently applicable are stored as excluded (a no-longer-
    // applicable section can never render anyway, so we don't persist its key).
    generering: {
      excluded_sections: [...excludedSections].filter(k => genSectionApplies(k)),
      extra_paragraphs: extraParagraphs
        .filter(p => p.title.trim() || p.content.trim())
        .map(p => ({ title: p.title.trim(), content: p.content.trim() })),
    },
  })
  const previewBody = () => ({
    ...createBody(),
    landlord_name: company?.name ?? null, landlord_cvr: company?.cvr ?? null,
    landlord_address: [company?.address, [company?.postnr, company?.by].filter(Boolean).join(' ')].filter(Boolean).join(', ') || null,
    property_address: lejemaal, unit_no: lease.unit_no, usage_type: lease.usage_type ?? 'erhverv', area_m2: lease.area_m2,
    tenant_parties: [
      ...(selectedRenter ? [{ name: selectedRenter.name, role: 'primary_tenant', party_kind: selectedRenter.party_kind, cvr_nr: selectedRenter.cvr_nr, email: selectedRenter.email, ean_number: selectedRenter.party_kind === 'selskab' ? (eanNumber || null) : null, invoice_email: selectedRenter.party_kind === 'selskab' ? (invoiceEmail || null) : null }] : []),
      ...extraParties.flatMap(ep => {
        const r = renters.find(x => x.id === ep.renter_id)
        return r ? [{ name: r.name, role: ep.role, party_kind: r.party_kind, cvr_nr: r.cvr_nr, email: r.email }] : []
      }),
    ],
    clauses: vilkaarItems.map(p => ({ title: punktTitle(p), content: punktText(p) })),
  })

  // ── Validation / step status ─────────────────────────────────────────────
  // Total leje: the sum of line-items if any carry an amount, else the single
  // monthly leje (rentDkk). The Generelt month-leje stays the back-compat source.
  // Only kategori='leje' rows count as rent — bidrag (faste bidrag) rows must not
  // satisfy the "leje > 0" gate, or the backend rejects monthly_rent_oere=0 (422).
  const lejeLinesTotalOere = lejeLines.filter(l => l.kategori !== 'bidrag').reduce((s, l) => s + toOere(l.belob_dkk), 0)
  const totalLejeOere = lejeLinesTotalOere > 0 ? lejeLinesTotalOere : toOere(rentDkk)

  const errors: ValidationError[] = []
  if (totalLejeOere <= 0) errors.push({ step: 0, stepName: 'Generelt', label: 'Månedlig leje' })
  if (!startDate) errors.push({ step: 0, stepName: 'Generelt', label: 'Begyndelsesdato' })
  if (!renterId) errors.push({ step: 6, stepName: 'Parter', label: 'Lejer' })

  const stepComplete = (i: number): boolean => {
    if (i === 0) return totalLejeOere > 0 && !!startDate
    if (i === 6) return !!renterId
    return true
  }
  const stepStatus = (i: number): StepStatus => {
    if (i === step) return 'active'
    if (i > maxVisited) return 'future'
    return stepComplete(i) ? 'done' : 'partial'
  }
  const goStep = (i: number) => { setView('form'); setStep(i); setMaxVisited(m => Math.max(m, i)) }
  const handlePreview = () => { setFetchKey(k => k + 1); setView('preview') }

  // A3 — light debounced autosave on step change. Persists progress (creating
  // the draft on first save, then PATCHing) so a reload mid-flow resumes. Gated
  // on the minimal required fields so mere navigation never orphans empty drafts.
  const firstStepRender = useRef(true)
  useEffect(() => {
    if (firstStepRender.current) { firstStepRender.current = false; return }
    if (busy || totalLejeOere <= 0 || !startDate) return
    const t = setTimeout(() => { ensureDraft().catch(() => { /* autosave is best-effort */ }) }, 600)
    return () => clearTimeout(t)
  }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ──────────────────────────────────────────────────────────────
  // Sync the vilkår punkter to contract_clauses — ONE atomic replace-all PUT,
  // so the stored clauses always mirror the punkt list (order = sort_order).
  async function syncClauses(id: string): Promise<void> {
    // Block the PUT until the resume-hydration GET has confirmed what is in DB.
    // Prevents a transient GET failure from causing autosave to wipe stored clauses.
    if (!clausesHydrated.current) return
    await apiFetch(`/api/v1/contracts/${id}/clauses`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clauses: vilkaarItems.map((p, i) => ({
        paragraph_number: punktTitle(p), content: punktText(p), sort_order: i })) }),
    })
  }
  async function ensureDraft(): Promise<string> {
    if (draftId) {
      await apiFetch(`/api/v1/contracts/${draftId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(createBody()) })
      await syncClauses(draftId)
      return draftId
    }
    const created = await apiFetch<{ id: string }>('/api/v1/contracts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(createBody()) })
    setDraftId(created.id)
    onDraftCreated?.(created.id)  // A3 — push ?draft=<id> so a reload resumes
    await syncClauses(created.id)
    return created.id
  }
  async function handleBankgarantiUpload(file: File) {
    setBgUploadError(null); setBgUploading(true)
    try {
      const id = await ensureDraft()  // the upload needs a persisted contract id
      const fd = new FormData()
      fd.append('file', file)
      const r = await apiFetch<{ bankgaranti_document_filename: string }>(
        `/api/v1/contracts/${id}/bankgaranti-document`, { method: 'POST', body: fd })
      setBgDocFilename(r.bankgaranti_document_filename)
    } catch (e) {
      setBgUploadError(e instanceof ApiError ? e.message : 'Upload mislykkedes')
    } finally {
      setBgUploading(false)
    }
  }
  async function handleSaveDraft() { setBusy(true); try { await ensureDraft() } finally { setBusy(false) } }
  async function handleSaveDraftAndClose() {
    // Only leave the wizard once the draft is actually saved — a failed save
    // (e.g. no leje yet → 422) used to throw uncaught and close, losing the input.
    setBusy(true)
    try {
      await ensureDraft()
      onCancel()
    } catch (e) {
      alert(`Kladden kunne ikke gemmes: ${e instanceof ApiError ? e.message : 'ukendt fejl'}`)
    } finally {
      setBusy(false)
    }
  }
  async function handleDeleteDraft() {
    if (draftId) { try { await apiFetch(`/api/v1/contracts/${draftId}`, { method: 'DELETE' }) } catch { /* ignore */ } }
    onCancel()
  }

  // ── Flow templates (A3 part 2) ─────────────────────────────────────────────
  // The template payload is the wizard's contract field-set — createBody() minus
  // the per-contract identity (lease/type/step). Stamped back via applyContractFields.
  const templatePayload = () => {
    const { lease_id: _l, type: _t, last_step: _s, ...fields } = createBody()
    void _l; void _t; void _s
    return {
      ...fields,
      // Vilkår punkter — stored lean: title/baseText re-resolve from the
      // library on apply, so library edits flow through to stamped contracts.
      vilkaar_punkter: vilkaarItems.map(p => p.kind === 'builtin'
        ? { kind: 'builtin' as const, key: p.key, ...(p.overrideText != null ? { overrideText: p.overrideText } : {}) }
        : { kind: 'custom' as const, text: p.text }),
    }
  }
  // Template apply — rebuild the punkt list from the stored serialization. A
  // builtin whose library row vanished degrades to a custom punkt when an
  // override preserved its text (else it is dropped). Legacy templates carry
  // erhverv_saerlige_vilkaar free-text instead → migrated to a custom punkt.
  // The library is fetched fresh here to avoid resolving builtins against a
  // stale (or still-loading) state snapshot; falls back to current state on
  // failure so a transient network error degrades gracefully.
  type StoredPunkt = { kind: string; key?: string | null; text?: string; overrideText?: string }
  const applyTemplateVilkaar = async (payload: Partial<ErhvervContract> & { vilkaar_punkter?: StoredPunkt[] }) => {
    const freshLib = await apiFetch<LibraryClause[]>('/api/v1/vilkaar/erhverv-library')
      .catch(() => library)
    setLibrary(freshLib)
    const punkter: ErhvervPunkt[] = []
    for (const [i, p] of (payload.vilkaar_punkter ?? []).entries()) {
      if (p.kind === 'builtin' && p.key) {
        const b = freshLib.find(x => x.source === 'builtin' && x.key === p.key)
        if (b) punkter.push({ kind: 'builtin', key: p.key, title: b.title ?? 'Vilkår', baseText: b.content, ...(p.overrideText != null ? { overrideText: p.overrideText } : {}) })
        else if (p.overrideText) punkter.push({ kind: 'custom', localId: `t-${i}`, text: p.overrideText })
      } else if (p.kind === 'custom' && p.text) {
        punkter.push({ kind: 'custom', localId: `t-${i}`, text: p.text })
      }
    }
    if (payload.erhverv_saerlige_vilkaar?.trim()) punkter.push({ kind: 'custom', localId: 'tmpl-legacy-sv', text: payload.erhverv_saerlige_vilkaar })
    setVilkaarItems(punkter)
  }
  async function handleSaveTemplate() {
    const name = templateName.trim()
    if (!name) { setTemplateMsg('Giv skabelonen et navn.'); return }
    setSavingTemplate(true); setTemplateMsg(null)
    try {
      await apiFetch('/api/v1/erhverv-templates', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, payload: templatePayload() }),
      })
      setTemplateName(''); setTemplateMsg(`Skabelon «${name}» gemt.`)
      loadTemplateList()
    } catch (e) {
      setTemplateMsg(e instanceof ApiError ? e.message : 'Kunne ikke gemme skabelonen.')
    } finally { setSavingTemplate(false) }
  }
  async function handleUseTemplate() {
    if (!selectedTemplateId) return
    const tmpl = templates.find(t => t.id === selectedTemplateId)
    if (!window.confirm(`Anvend skabelonen «${tmpl?.name ?? ''}»? Felterne i kontrakten overskrives (lejemål og lejer bevares).`)) return
    setTemplateMsg(null)
    try {
      const full = await apiFetch<TemplateResponse>(`/api/v1/erhverv-templates/${selectedTemplateId}`)
      applyContractFields(full.payload)
      await applyTemplateVilkaar(full.payload)
      setTemplateMsg(`Skabelon «${full.name}» anvendt.`)
    } catch (e) {
      setTemplateMsg(e instanceof ApiError ? e.message : 'Kunne ikke indlæse skabelonen.')
    }
  }
  async function handleDeleteTemplate() {
    if (!selectedTemplateId) return
    const tmpl = templates.find(t => t.id === selectedTemplateId)
    if (!window.confirm(`Slet skabelonen «${tmpl?.name ?? ''}»?`)) return
    try {
      await apiFetch(`/api/v1/erhverv-templates/${selectedTemplateId}`, { method: 'DELETE' })
      setSelectedTemplateId(''); loadTemplateList()
    } catch { setTemplateMsg('Kunne ikke slette skabelonen.') }
  }
  async function handleSend() {
    setBusy(true)
    let id: string
    try { id = await ensureDraft() } catch { setBusy(false); return }
    try {
      // Persist the Lejer-step nuances (C1) on the picked renter before linking.
      if (renterId) {
        await apiFetch(`/api/v1/renters/${renterId}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            manual_address: manualAddress,
            address_street: manualAddress ? (addressStreet || null) : null,
            address_postnr: manualAddress ? (addressPostnr || null) : null,
            address_by: manualAddress ? (addressBy || null) : null,
            address_land: manualAddress ? (addressLand || null) : null,
            no_email: noEmail,
            prefers_letter: prefersLetter,
            no_cpr: noCpr,
            iban: noCpr ? (iban || null) : null,
            swift: noCpr ? (swift || null) : null,
            // F5 — selskab e-faktura routing.
            ean_number: selectedRenter?.party_kind === 'selskab' ? (eanNumber || null) : null,
            invoice_email: selectedRenter?.party_kind === 'selskab' ? (invoiceEmail || null) : null,
          }),
        }).catch(() => { /* nuances are best-effort; do not block signing */ })
      }
      await apiFetch(`/api/v1/contracts/${id}/parties`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ renter_id: renterId, role: 'primary_tenant' }) })
      // Extra parties (medlejere/kautionister) — skip any that duplicate the
      // primary lejer or are unset.
      for (const ep of extraParties) {
        if (!ep.renter_id || ep.renter_id === renterId) continue
        await apiFetch(`/api/v1/contracts/${id}/parties`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ renter_id: ep.renter_id, role: ep.role }) })
      }
      // Vilkår punkter are already synced (one atomic PUT) by ensureDraft above.
      // Idura MitID signing (step F): create the signature order — this renders
      // the PDF, creates one signatory per party (landlord + tenants), emails
      // each their personal MitID link and flips signing_status → 'sent'. On
      // success we stay on the Oversigt step and show the "sendt" state with
      // each party's signing href + status (polled below).
      try {
        const result = await createSigningOrder(id)
        setSentResult(result)
        setSentStatus(null)
        setStep(8)  // land on Oversigt where the sent-state UI lives
      } catch (e) {
        // Idura unavailable (route missing / older backend) → fall back to the
        // legacy local-sign stub so the existing send flow keeps working.
        if (e instanceof ApiError && e.status === 404) {
          await apiFetch(`/api/v1/contracts/${id}/sign`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ party: 'landlord' }) })
          await apiFetch(`/api/v1/contracts/${id}/sign`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ party_id: renterId }) })
          onDone(id)
          return
        }
        setSendError(e instanceof ApiError ? e.message : 'Kunne ikke sende til underskrift.')
      }
    } finally { setBusy(false) }
  }

  // Step F — poll the signing order status while the sent-state UI is open, so
  // each party's afventer/underskrevet flips live as webhooks land. Stops once
  // the order is completed.
  useEffect(() => {
    if (!sentResult || !draftId) return
    let cancelled = false
    const poll = async () => {
      const s = await getSigningOrder(draftId)
      if (!cancelled && s) setSentStatus(s)
      return s
    }
    poll()
    const t = setInterval(async () => {
      const s = await poll()
      if (s && (s.signing_status === 'completed' || s.signed)) clearInterval(t)
    }, 5000)
    return () => { cancelled = true; clearInterval(t) }
  }, [sentResult, draftId])

  // Party display name for the sent-state list: landlord = company, tenant = renter.
  const partyLabel = (role: string, renter_id: string | null): string => {
    if (role === 'landlord') return company?.name ? `${company.name} (udlejer)` : 'Udlejer'
    const r = renters.find(x => x.id === renter_id)
    return r?.name ?? (role === 'co_tenant' ? 'Medlejer' : 'Lejer')
  }
  // Has this signatory signed yet? (matched by signatory_id from the status poll.)
  const isSigned = (signatoryId: string): boolean =>
    !!sentStatus?.signatories.find(s => s.signatory_id === signatoryId)?.signed_at

  const num = (v: string) => v.replace(/[^\d.,]/g, '')

  // D4 — shared "Grænse for regulering" caps block: a Type selector
  // (Procentvis/Fast beløb) switching the min/max inputs between % and kr.
  // Used by both the Nettopris and Indeks sub-forms.
  const capsSection = (
    <>
      <SubSectionDivider title="Grænse for regulering" />
      <FieldRow label="Type">
        <select style={inputStyle} value={reguleringCapType} onChange={e => setReguleringCapType(e.target.value as 'procent' | 'beloeb')}>
          <option value="procent">Procentvis</option>
          <option value="beloeb">Fast beløb</option>
        </select>
      </FieldRow>
      {reguleringCapType === 'procent' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FieldRow label="Min. % pr. år"><input style={inputStyle} value={reguleringMinPct} inputMode="decimal" placeholder="—" onChange={e => setReguleringMinPct(num(e.target.value))} /></FieldRow>
          <FieldRow label="Maks. % pr. år"><input style={inputStyle} value={reguleringMaxPct} inputMode="decimal" placeholder="—" onChange={e => setReguleringMaxPct(num(e.target.value))} /></FieldRow>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FieldRow label="Min. kr. pr. år"><input style={inputStyle} value={reguleringMinBeloeb} inputMode="decimal" placeholder="—" onChange={e => setReguleringMinBeloeb(num(e.target.value))} /></FieldRow>
          <FieldRow label="Maks. kr. pr. år"><input style={inputStyle} value={reguleringMaxBeloeb} inputMode="decimal" placeholder="—" onChange={e => setReguleringMaxBeloeb(num(e.target.value))} /></FieldRow>
        </div>
      )}
    </>
  )

  // D5 — shared "Varslingsskrivelse" block: a toggle (default Fra) + a per-type
  // helper note. The reguleringsbrev PDF + endpoint already exist; this only
  // stores the flag. Used by every regulering sub-form.
  const varslingLejerTiming = sendVarslingAuto
    ? 'Lejer får automatisk varslingsskrivelsen på e-mail samtidig med din påmindelse.'
    : 'Lejer vil modtage varslingsskrivelsen 14 dage efter din modtagelse af varslingen.'
  const varslingHelp = reguleringType === 'nettopris'
    ? `Du vil modtage varslingsskrivelsen 3 måneder før reguleringen, eller så snart Nettoprisindekset bliver offentliggjort. ${varslingLejerTiming}`
    : reguleringType === 'indeks'
      ? `Du vil modtage varslingsskrivelsen 3 måneder før reguleringen, eller så snart indeksene bliver offentliggjort. ${varslingLejerTiming}`
      : `Du vil modtage varslingsskrivelsen 3 måneder før reguleringen. ${varslingLejerTiming}`
  const varslingSection = (
    <>
      <SubSectionDivider title="Varslingsskrivelse" />
      <FieldRow label="Skal lejer have en varslingsskrivelse?" help={sendVarsling ? varslingHelp : undefined}>
        <Toggle on={sendVarsling} onToggle={() => setSendVarsling(v => !v)} />
      </FieldRow>
      {sendVarsling && (
        <FieldRow
          label="Send varslingen automatisk?"
          help={sendVarslingAuto
            ? 'Lejer får automatisk varslingsskrivelsen på e-mail, når påmindelsen forfalder — du behøver ikke selv at sende den.'
            : 'Slået fra: du sender selv varslingsskrivelsen til lejer.'}
        >
          <Toggle on={sendVarslingAuto} onToggle={() => setSendVarslingAuto(v => !v)} />
        </FieldRow>
      )}
      {sendVarsling && (
        <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 0' }}>
          Boligflow genererer dette dokument baseret på de oplysninger du har indtastet. Vi tilbyder værktøjer til at understøtte din administration — ikke juridisk rådgivning. Ved tvivl, kontakt en advokat.
        </p>
      )}
    </>
  )

  // ── Sections (cards, identical primitives to the bolig wizard) ─────────────
  const sections: React.ReactNode[] = [
    // 0 — Generelt (lejemål + formål + periode + leje & moms)
    <div key="g">
      <SectionBlock icon={IcoDoc} title="Skabeloner" subtitle="Gem den nuværende opsætning som skabelon, eller anvend en gemt skabelon">
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
          En skabelon gemmer kontraktens felter (moms, regulering, leje, forbrug, betalinger, vilkår) — ikke lejemål eller lejer. Anvend en skabelon for at udfylde en ny kontrakt med en kendt opsætning.
        </p>
        <FieldRow label="Gem som skabelon" help="Giv opsætningen et navn og gem den til genbrug.">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
            <input style={inputStyle} value={templateName} placeholder="F.eks. Standard kontor" onChange={e => setTemplateName(e.target.value)} />
            <button type="button" onClick={handleSaveTemplate} disabled={savingTemplate || !templateName.trim()} style={{ fontSize: 13, fontWeight: 500, color: 'white', background: !templateName.trim() ? '#d1d5db' : BLUE, border: 'none', borderRadius: 7, padding: '0 16px', cursor: !templateName.trim() ? 'not-allowed' : 'pointer' }}>{savingTemplate ? 'Gemmer…' : 'Gem skabelon'}</button>
          </div>
        </FieldRow>
        {templates.length > 0 && (
          <FieldRow label="Brug skabelon" help="Vælg en gemt skabelon og anvend den. De nuværende felter overskrives.">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8 }}>
              <select style={inputStyle} value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)}>
                <option value="">Vælg skabelon</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button type="button" onClick={handleUseTemplate} disabled={!selectedTemplateId} style={{ fontSize: 13, fontWeight: 500, color: 'white', background: !selectedTemplateId ? '#d1d5db' : BLUE, border: 'none', borderRadius: 7, padding: '0 16px', cursor: !selectedTemplateId ? 'not-allowed' : 'pointer' }}>Brug</button>
              <button type="button" onClick={handleDeleteTemplate} disabled={!selectedTemplateId} title="Slet skabelon" style={{ fontSize: 13, color: !selectedTemplateId ? '#d1d5db' : '#dc2626', background: 'white', border: '1px solid #e5e7eb', borderRadius: 7, padding: '0 12px', cursor: !selectedTemplateId ? 'not-allowed' : 'pointer' }}>Slet</button>
            </div>
          </FieldRow>
        )}
        {templateMsg && <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>{templateMsg}</div>}
      </SectionBlock>
      <SectionBlock icon={IcoHouse} title="Lejemål" subtitle="Hvilket erhvervslejemål udlejes?">
        <FieldRow label="Lejemål"><div style={{ fontSize: 14, color: '#111827', padding: '4px 0' }}>{lejemaal || lease.unit_no} · {USAGE_LABELS[lease.usage_type ?? ''] ?? 'Erhverv'}</div></FieldRow>
        <FieldRow label="Anvendelse og formål"><input style={inputStyle} value={formaal} placeholder="F.eks. detailhandel, kontor, lager" onChange={e => setFormaal(e.target.value)} /></FieldRow>
      </SectionBlock>
      <SectionBlock icon={IcoCal} title="Lejeperiode" subtitle="Begyndelse, ophør og uopsigelighed">
        <FieldRow label="Begyndelsesdato" required><input type="date" style={inputStyle} value={startDate} onChange={e => setStartDate(e.target.value)} /></FieldRow>
        <FieldRow label="Er lejeforholdet tidsbegrænset?" help="Slå til for at angive en ophørsdato."><Toggle on={tidsbegraenset} onToggle={() => setTidsbegraenset(v => !v)} /></FieldRow>
        {tidsbegraenset && (
          <>
            <FieldRow label="Ophørsdato" required><input type="date" style={inputStyle} value={endDate} onChange={e => setEndDate(e.target.value)} /></FieldRow>
            <FieldRow label="Er lejemålet opsigeligt i perioden?" help="Slå til hvis det tidsbegrænsede lejeforhold kan opsiges inden ophørsdatoen. Ellers er det uopsigeligt i perioden."><Toggle on={tidsbegraensetOpsigelig} onToggle={() => setTidsbegraensetOpsigelig(v => !v)} /></FieldRow>
          </>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
          <FieldRow label="Opsigelsesvarsel">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input style={inputStyle} value={opsigelsesvarsel} inputMode="numeric" onChange={e => setOpsigelsesvarsel(num(e.target.value))} />
              <select style={inputStyle} value={opsigelsesEnhed} onChange={e => setOpsigelsesEnhed(e.target.value)}>
                <option value="dag">Dag</option>
                <option value="uge">Uge</option>
                <option value="maaned">Måned</option>
                <option value="aar">År</option>
              </select>
            </div>
          </FieldRow>
          <FieldRow label="Afståelsesret"><Toggle on={afstaaelsesret} onToggle={() => setAfstaaelsesret(v => !v)} /></FieldRow>
        </div>
        <FieldRow label="Skal lejeforholdet være uopsigeligt i en periode?"><Toggle on={uopsigelig} onToggle={() => setUopsigelig(v => !v)} /></FieldRow>
        {uopsigelig && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
            <FieldRow label="Uopsigelig for lejer">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <input style={inputStyle} value={uopsigLejerVal} inputMode="numeric" placeholder="5" onChange={e => setUopsigLejerVal(num(e.target.value))} />
                <select style={inputStyle} value={uopsigLejerEnhed} onChange={e => setUopsigLejerEnhed(e.target.value)}>
                  <option value="dag">Dag</option>
                  <option value="uge">Uge</option>
                  <option value="maaned">Måned</option>
                  <option value="aar">År</option>
                </select>
              </div>
            </FieldRow>
            <FieldRow label="Uopsigelig for udlejer">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <input style={inputStyle} value={uopsigUdlejerVal} inputMode="numeric" placeholder="5" onChange={e => setUopsigUdlejerVal(num(e.target.value))} />
                <select style={inputStyle} value={uopsigUdlejerEnhed} onChange={e => setUopsigUdlejerEnhed(e.target.value)}>
                  <option value="dag">Dag</option>
                  <option value="uge">Uge</option>
                  <option value="maaned">Måned</option>
                  <option value="aar">År</option>
                </select>
              </div>
            </FieldRow>
          </div>
        )}
        <FieldRow label="Skal lejer fraflytte 14 dage før ophør?" help="Informationsfelt: lejer fraflytter 14 dage før ophørsdato, så istandsættelse kan ske inden lejemålets ophør."><Toggle on={istandsaettelse14} onToggle={() => setIstandsaettelse14(v => !v)} /></FieldRow>
      </SectionBlock>
      <SectionBlock icon={IcoCal} title="Indflytningssyn" subtitle="Planlæg syn og påmindelse">
        <FieldRow label="Vil du planlægge indflytningssyn?"><Toggle on={planlaegSyn} onToggle={() => setPlanlaegSyn(v => !v)} /></FieldRow>
        {planlaegSyn && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FieldRow label="Dato for indflytningssyn"><input type="date" style={inputStyle} value={inspectionDate} onChange={e => setInspectionDate(e.target.value)} /></FieldRow>
              <FieldRow label="Tidspunkt"><input type="time" style={inputStyle} value={inspectionTime} onChange={e => setInspectionTime(e.target.value)} /></FieldRow>
            </div>
            <FieldRow label="Send påmindelse på e-mail" help="Sender en påmindelse til lejer dagen før indflytningssynet."><Toggle on={synEmailReminder} onToggle={() => setSynEmailReminder(v => !v)} /></FieldRow>
          </>
        )}
      </SectionBlock>
      <SectionBlock icon={IcoCash} title="Husleje" subtitle="Hvad er lejen, og er lejemålet momspligtigt?">
        <FieldRow label="Månedlig leje (ekskl. moms)" required help="Beløb i kr. Forbrug afregnes særskilt. Brug trinnet Leje for at opsplitte lejen i flere poster."><input style={inputStyle} value={rentDkk} inputMode="decimal" placeholder="6.666" onChange={e => setRentDkk(num(e.target.value))} /></FieldRow>
        <FieldRow label="Momspligtigt (frivillig momsregistrering — 25%)" help="Bekræftes af revisor før produktion."><Toggle on={momspligtig} onToggle={() => setMomspligtig(v => !v)} /></FieldRow>
      </SectionBlock>
    </div>,

    // 1 — Leje (line-item table: konto / tekst / beløb / momspligtig)
    <div key="leje">
      <SectionBlock icon={IcoCash} title="Leje" subtitle="Opsplit lejen i poster, eller lad stå tom for én samlet leje">
        <FieldRow label="Opkrævningsinterval" help="Hvor ofte lejen opkræves forud (angives i §Leje).">
          <select style={inputStyle} value={opkraevningsinterval} onChange={e => setOpkraevningsinterval(e.target.value)}>
            {OPKRAEVNINGSINTERVAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FieldRow>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '12px 0' }}>
          Tilføj en linje pr. lejepost (f.eks. grundleje, fællesudgifter). Lad tabellen stå tom for at bruge den samlede månedlige leje fra trinnet Generelt. Linjer med samme gruppe (f.eks. «§§ 117/119») vises samlet på én linje for lejer. Faste bidrag (internet/TV aconto m.v.) opkræves sammen med lejen, men vises i deres egen sektion og tæller ikke med i lejen. Moms × opsplittet leje bekræftes af revisor før produktion.
        </p>
        {lejeLines.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {lejeLines.map((l, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 0.9fr 1fr 0.9fr auto auto', gap: 8, alignItems: 'center' }}>
                {kontoOptions.length > 0 ? (
                  <select style={inputStyle} value={l.gl_account} onChange={e => setLejeLines(rows => rows.map((x, j) => j === i ? { ...x, gl_account: e.target.value } : x))}>
                    <option value="">Vælg konto</option>
                    {kontoOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input style={inputStyle} placeholder="Konto" value={l.gl_account} onChange={e => setLejeLines(rows => rows.map((x, j) => j === i ? { ...x, gl_account: e.target.value } : x))} />
                )}
                <input style={inputStyle} placeholder="Tekst" value={l.tekst} onChange={e => setLejeLines(rows => rows.map((x, j) => j === i ? { ...x, tekst: e.target.value } : x))} />
                <input style={inputStyle} placeholder="Beløb (kr.)" inputMode="decimal" value={l.belob_dkk} onChange={e => setLejeLines(rows => rows.map((x, j) => j === i ? { ...x, belob_dkk: num(e.target.value) } : x))} />
                <input style={inputStyle} placeholder="Gruppe (valgfri)" value={l.gruppe} disabled={l.kategori === 'bidrag'} onChange={e => setLejeLines(rows => rows.map((x, j) => j === i ? { ...x, gruppe: e.target.value } : x))} />
                <select style={inputStyle} value={l.kategori} onChange={e => setLejeLines(rows => rows.map((x, j) => j === i ? { ...x, kategori: e.target.value as 'leje' | 'bidrag', gruppe: e.target.value === 'bidrag' ? '' : x.gruppe } : x))}>
                  <option value="leje">Leje</option>
                  <option value="bidrag">Fast bidrag</option>
                </select>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title="Momspligtig">
                  <Toggle on={l.momspligtig} onToggle={() => setLejeLines(rows => rows.map((x, j) => j === i ? { ...x, momspligtig: !x.momspligtig } : x))} />
                </div>
                <button type="button" onClick={() => setLejeLines(rows => rows.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }} aria-label="Fjern linje">×</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 16, marginTop: lejeLines.length > 0 ? 10 : 0 }}>
          <button type="button" onClick={() => setLejeLines(rows => [...rows, { gl_account: '', tekst: '', belob_dkk: '', momspligtig: momspligtig, kategori: 'leje', gruppe: '' }])} style={{ alignSelf: 'flex-start', fontSize: 13, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Tilføj lejelinje</button>
          <button type="button" onClick={() => setLejeLines(rows => [...rows, { gl_account: '', tekst: '', belob_dkk: '', momspligtig: momspligtig, kategori: 'bidrag', gruppe: '' }])} style={{ alignSelf: 'flex-start', fontSize: 13, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Tilføj fast bidrag</button>
        </div>
        {lejeLinesTotalOere > 0 && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#374151' }}>
            Total leje ekskl. moms: <b>{(lejeLines.filter(l => l.kategori !== 'bidrag').reduce((s, l) => s + toOere(l.belob_dkk), 0) / 100).toLocaleString('da-DK', { minimumFractionDigits: 2 })} kr.</b>
          </div>
        )}
      </SectionBlock>
    </div>,

    // 2 — Forbrug (A1) — afregning pr. forbrugstype + internet/TV bidrag + fuldmagt
    <div key="forbrug">
      <SectionBlock icon={IcoBolt} title="Forbrug" subtitle="Hvordan afregnes el, vand, varme og køling?">
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
          Vælg afregningsform pr. forbrugstype. Vælges «Aconto til udlejer» kan du angive et acontobeløb, der opkræves sammen med lejen og vises i kontraktens forbrugsafsnit. «Aconto til udlejer» og «Inkluderet i lejen» kræver, at lejemålet er tilkoblet forbrugstypen på ejendommen.
        </p>
        {FORBRUG_TYPES.map(({ key, label }) => {
          const t = forbrugTyper[key]
          const available = forbrugAvailability[key]
          const options = AFREGNING_OPTIONS.map(o => ({
            ...o,
            disabled: !available && FORBRUGSGRUPPE_GATED_AFREGNING.includes(o.value as Afregning),
          }))
          const set = (patch: Partial<ForbrugTypeRow>) => setForbrugTyper(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }))
          return (
            <div key={key} style={{ marginBottom: 16 }}>
              <SubSectionDivider title={`Afregning af ${label}`} />
              <RadioPillList options={options} value={t.afregning} onChange={v => set({ afregning: (v || 'mellem_lejer_forsyning') as Afregning })} />
              {!available && (
                <>
                  <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 6, marginBottom: 0 }}>{FORBRUG_UNAVAILABLE_NOTE}</p>
                  <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, marginBottom: 0 }}>{FORBRUG_UNLOCK_HINT}</p>
                </>
              )}
              {t.afregning === 'aconto_udlejer' && (
                <FieldRow label="Acontobeløb (kr. pr. md.)" help="Beløbet opkræves som aconto sammen med lejen og vises i kontraktens forbrugsafsnit."><input style={inputStyle} value={t.aconto_dkk} inputMode="decimal" placeholder="Indtast beløb" onChange={e => set({ aconto_dkk: num(e.target.value) })} /></FieldRow>
              )}
              <FieldRow label="Indhent fuldmagt fra lejer" help="Fuldmagten underskrives sammen med lejekontrakten (udlejer håndterer tilmelding). Du skal selv sende tilmeldingen afsted til forsyningsselskabet."><Toggle on={t.fuldmagt} onToggle={() => set({ fuldmagt: !t.fuldmagt })} /></FieldRow>
            </div>
          )
        })}
      </SectionBlock>
      <SectionBlock icon={IcoCash} title="Bidrag til internet og TV" subtitle="Skal lejer betale bidrag til internet eller TV?">
        <FieldRow label="Skal lejer betale bidrag til internet?"><Toggle on={internetBidrag} onToggle={() => setInternetBidrag(v => !v)} /></FieldRow>
        {internetBidrag && (
          <FieldRow label="Internet — acontobeløb (kr. pr. md.)" help="Vises i kontraktens forbrugsafsnit og opkræves sammen med lejen."><input style={inputStyle} value={internetAcontoDkk} inputMode="decimal" placeholder="Indtast beløb" onChange={e => setInternetAcontoDkk(num(e.target.value))} /></FieldRow>
        )}
        <FieldRow label="Skal lejer betale bidrag til TV?"><Toggle on={tvBidrag} onToggle={() => setTvBidrag(v => !v)} /></FieldRow>
        {tvBidrag && (
          <>
            <FieldRow label="Hvem leverer TV-pakke?">
              <RadioPillList
                options={[{ value: 'udlejer', label: 'Udlejer leverer TV-pakke' }, { value: 'antenneforening', label: 'Lejernes antenneforening leverer TV-pakke' }]}
                value={tvProvider}
                onChange={v => setTvProvider((v || 'udlejer') as 'udlejer' | 'antenneforening')}
              />
            </FieldRow>
            <FieldRow label="TV — acontobeløb (kr. pr. md.)" help="Vises i kontraktens forbrugsafsnit og opkræves sammen med lejen."><input style={inputStyle} value={tvAcontoDkk} inputMode="decimal" placeholder="Indtast beløb" onChange={e => setTvAcontoDkk(num(e.target.value))} /></FieldRow>
          </>
        )}
      </SectionBlock>
    </div>,

    // 3 — Betalinger (depositum/forudbetalt måneder + beløb, bankgaranti, indflytningsopkrævning)
    <div key="betal">
      <SectionBlock icon={IcoShield} title="Depositum og forudbetalt leje" subtitle="Sikkerhedsstillelse ved lejemålets begyndelse">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FieldRow label="Depositum — antal måneder" help="Vælg et antal måneder, så beregner Boligflow beløbet ud fra den månedlige leje.">
            <select style={inputStyle} value={depositumMode} onChange={e => {
              const m = e.target.value as typeof depositumMode
              setDepositumMode(m)
              if (m === 'ingen') { setDepositumMaaneder('') }
              else if (m === 'andet') { setDepositumMaaneder('') }
            }}>
              <option value="ingen">Ingen</option>
              <option value="1">1 måned</option>
              <option value="2">2 måneder</option>
              <option value="3">3 måneder</option>
              <option value="andet">Andet</option>
            </select>
          </FieldRow>
          <FieldRow label="Depositum (kr.)"><input style={inputStyle} value={depositDkk} inputMode="decimal" placeholder="19.998" disabled={depositumMode !== 'ingen' && depositumMode !== 'andet'} onChange={e => setDepositDkk(num(e.target.value))} /></FieldRow>
        </div>
        {depositumMode === 'andet' && (
          <FieldRow label="Depositum — antal måneder (frit)"><input style={inputStyle} value={depositumMaaneder} inputMode="numeric" placeholder="6" onChange={e => setDepositumMaaneder(e.target.value.replace(/[^\d]/g, '').slice(0, 2))} /></FieldRow>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FieldRow label="Forudbetalt leje — antal måneder" help="Vælg et antal måneder, så beregner Boligflow beløbet ud fra den månedlige leje.">
            <select style={inputStyle} value={forudbetaltMode} onChange={e => {
              const m = e.target.value as typeof forudbetaltMode
              setForudbetaltMode(m)
              if (m === 'ingen') { setForudbetaltMaaneder('') }
              else if (m === 'andet') { setForudbetaltMaaneder('') }
            }}>
              <option value="ingen">Ingen</option>
              <option value="1">1 måned</option>
              <option value="2">2 måneder</option>
              <option value="3">3 måneder</option>
              <option value="andet">Andet</option>
            </select>
          </FieldRow>
          <FieldRow label="Forudbetalt leje (kr.)"><input style={inputStyle} value={prepaidDkk} inputMode="decimal" placeholder="19.998" disabled={forudbetaltMode !== 'ingen' && forudbetaltMode !== 'andet'} onChange={e => setPrepaidDkk(num(e.target.value))} /></FieldRow>
        </div>
        {forudbetaltMode === 'andet' && (
          <FieldRow label="Forudbetalt leje — antal måneder (frit)"><input style={inputStyle} value={forudbetaltMaaneder} inputMode="numeric" placeholder="2" onChange={e => setForudbetaltMaaneder(e.target.value.replace(/[^\d]/g, '').slice(0, 2))} /></FieldRow>
        )}
        <FieldRow label="Bankgaranti" help="Lejer stiller bankgaranti i stedet for / ud over depositum."><Toggle on={bankgaranti} onToggle={() => setBankgaranti(v => !v)} /></FieldRow>
        <FieldRow label="Betalingsservice" help="Lejen tilmeldes Betalingsservice. Selve tilmeldingen oprettes hos Nets/Betalingsservice som en særskilt aftale — Boligflow gemmer kun ønsket."><Toggle on={betalingsserviceTilmeld} onToggle={() => setBetalingsserviceTilmeld(v => !v)} /></FieldRow>
        {bankgaranti && (
          <>
            <FieldRow label="Har du modtaget bankgarantien?"><Toggle on={bgModtaget} onToggle={() => setBgModtaget(v => !v)} /></FieldRow>
            {!bgModtaget && (
              <FieldRow label="Hvornår forventer du at modtage bankgarantien?" help="Boligflow minder dig om at oprette bankgarantien på denne dato, hvis den fortsat mangler.">
                <input type="date" style={inputStyle} value={bgForventetDato} onChange={e => setBgForventetDato(e.target.value)} />
              </FieldRow>
            )}
            {bgModtaget && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <FieldRow label="Garantibeløb (kr.)"><input style={inputStyle} value={bgBeloebKr} inputMode="decimal" placeholder="Indtast beløb" onChange={e => setBgBeloebKr(num(e.target.value))} /></FieldRow>
                  <FieldRow label="Antal måneders leje garantien dækker"><input style={inputStyle} value={bgMaaneder} inputMode="numeric" placeholder="3" onChange={e => setBgMaaneder(e.target.value.replace(/[^\d]/g, '').slice(0, 2))} /></FieldRow>
                </div>
                <FieldRow label="Garantistiller (bank)"><input style={inputStyle} value={bgStiller} placeholder="Indtast navn på bank" onChange={e => setBgStiller(e.target.value)} /></FieldRow>
                <FieldRow label="Garantistillers adresse"><input style={inputStyle} value={bgStillerAdresse} placeholder="Indtast adresse på bank" onChange={e => setBgStillerAdresse(e.target.value)} /></FieldRow>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                  <FieldRow label="Postnr."><input style={inputStyle} value={bgStillerPostnr} placeholder="Postnummer" onChange={e => setBgStillerPostnr(e.target.value)} /></FieldRow>
                  <FieldRow label="By"><input style={inputStyle} value={bgStillerBy} placeholder="By" onChange={e => setBgStillerBy(e.target.value)} /></FieldRow>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <FieldRow label="Dato for modtagelse"><input type="date" style={inputStyle} value={bgModtagetDato} onChange={e => setBgModtagetDato(e.target.value)} /></FieldRow>
                  <FieldRow label="Gyldig til"><input type="date" style={inputStyle} value={bgGyldigTil} onChange={e => setBgGyldigTil(e.target.value)} /></FieldRow>
                </div>
                <FieldRow label="Note" help="Ekstra noter omkring bankgarantien, hvis der er aftalt særlige vilkår."><input style={inputStyle} value={bgNote} placeholder="Indtast noter om bankgarantien" onChange={e => setBgNote(e.target.value)} /></FieldRow>
                <FieldRow label="Kopi af bankgarantien" help="Upload bankgarantien som PDF eller billede (maks. 10 MB).">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input type="file" accept="application/pdf,image/png,image/jpeg" disabled={bgUploading} onChange={e => { const f = e.target.files?.[0]; if (f) handleBankgarantiUpload(f) }} style={{ fontSize: 13 }} />
                    {bgUploading && <span style={{ fontSize: 12, color: '#6b7280' }}>Uploader…</span>}
                    {bgDocFilename && !bgUploading && <span style={{ fontSize: 12, color: '#16a34a' }}>Gemt: {bgDocFilename}</span>}
                    {bgUploadError && <span style={{ fontSize: 12, color: '#dc2626' }}>{bgUploadError}</span>}
                  </div>
                </FieldRow>
              </>
            )}
          </>
        )}
      </SectionBlock>
      <SectionBlock icon={IcoCash} title="Andre engangsbeløb" subtitle="Engangsbeløb ud over leje og depositum (f.eks. nøglepenge, skiltning)">
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
          Tilføj en linje pr. engangsbeløb. Beløb angives ekskl. moms. Moms × engangsbeløb bekræftes af revisor før produktion.
        </p>
        {engangsbeloeb.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {engangsbeloeb.map((e, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.4fr 1fr auto auto', gap: 8, alignItems: 'center' }}>
                <input style={inputStyle} placeholder="Type (f.eks. Nøglepenge)" value={e.bidragstype} onChange={ev => setEngangsbeloeb(rows => rows.map((x, j) => j === i ? { ...x, bidragstype: ev.target.value } : x))} />
                <input style={inputStyle} placeholder="Tekst" value={e.tekst} onChange={ev => setEngangsbeloeb(rows => rows.map((x, j) => j === i ? { ...x, tekst: ev.target.value } : x))} />
                <input style={inputStyle} placeholder="Beløb (kr.)" inputMode="decimal" value={e.belob_dkk} onChange={ev => setEngangsbeloeb(rows => rows.map((x, j) => j === i ? { ...x, belob_dkk: num(ev.target.value) } : x))} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title="Momspligtig">
                  <Toggle on={e.momspligtig} onToggle={() => setEngangsbeloeb(rows => rows.map((x, j) => j === i ? { ...x, momspligtig: !x.momspligtig } : x))} />
                </div>
                <button type="button" onClick={() => setEngangsbeloeb(rows => rows.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }} aria-label="Fjern engangsbeløb">×</button>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={() => setEngangsbeloeb(rows => [...rows, { bidragstype: '', tekst: '', belob_dkk: '', momspligtig: momspligtig }])} style={{ alignSelf: 'flex-start', marginTop: engangsbeloeb.length > 0 ? 10 : 0, fontSize: 13, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Tilføj engangsbeløb</button>
      </SectionBlock>
      <SectionBlock icon={IcoCash} title="Indflytningsopkrævning" subtitle="Opkrævning af depositum, forudbetalt og første periode">
        <FieldRow label="Skal Boligflow lave indflytningsopkrævningen?" help="Oprettes ved aktivering og sendes sammen med lejekontrakten."><Toggle on={indflytningsopkraevning} onToggle={() => setIndflytningsopkraevning(v => !v)} /></FieldRow>
        {indflytningsopkraevning && (
          <>
            <FieldRow label="Forfaldsdato"><input type="date" style={inputStyle} value={indflytningForfald} onChange={e => setIndflytningForfald(e.target.value)} /></FieldRow>
            <FieldRow label="Opsplit opkrævningen i flere poster?" help="Slå til for at give hver post (depositum, forudbetalt, første periode) sin egen forfaldsdato."><Toggle on={indflytningOpsplit} onToggle={() => setIndflytningOpsplit(v => !v)} /></FieldRow>
            {indflytningOpsplit && (
              <>
                {indflytningPoster.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {indflytningPoster.map((p, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                        <input style={inputStyle} placeholder="Post (f.eks. Depositum)" value={p.post} onChange={e => setIndflytningPoster(rows => rows.map((x, j) => j === i ? { ...x, post: e.target.value } : x))} />
                        <input style={inputStyle} placeholder="Beløb (kr.)" inputMode="decimal" value={p.belob_dkk} onChange={e => setIndflytningPoster(rows => rows.map((x, j) => j === i ? { ...x, belob_dkk: num(e.target.value) } : x))} />
                        <input type="date" style={inputStyle} value={p.forfaldsdato} onChange={e => setIndflytningPoster(rows => rows.map((x, j) => j === i ? { ...x, forfaldsdato: e.target.value } : x))} />
                        <button type="button" onClick={() => setIndflytningPoster(rows => rows.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }} aria-label="Fjern post">×</button>
                      </div>
                    ))}
                  </div>
                )}
                <button type="button" onClick={() => setIndflytningPoster(rows => [...rows, { post: '', belob_dkk: '', forfaldsdato: '' }])} style={{ alignSelf: 'flex-start', marginTop: indflytningPoster.length > 0 ? 10 : 0, fontSize: 13, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Tilføj post</button>
              </>
            )}
          </>
        )}
      </SectionBlock>
      {indflytningsopgoerelse && indflytningsopgoerelse.lines.length > 0 && (
        <SectionBlock icon={IcoCash} title="Indflytningsopgørelse (beregnet)" subtitle="Forventet opkrævning ved indflytning — beregnes automatisk">
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
            Read-only overslag. Leje for perioden beregnes fra begyndelsesdato til månedens udgang.{momspligtig ? ' Momsen bekræftes af revisor før produktion.' : ''}
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: '#6b7280', textAlign: 'right' }}>
                <th style={{ textAlign: 'left', padding: '4px 0', fontWeight: 500 }}>Post</th>
                <th style={{ padding: '4px 0', fontWeight: 500 }}>Ekskl. moms</th>
                {momspligtig && <th style={{ padding: '4px 0', fontWeight: 500 }}>Moms</th>}
                <th style={{ padding: '4px 0', fontWeight: 500 }}>Inkl. moms</th>
              </tr>
            </thead>
            <tbody>
              {indflytningsopgoerelse.lines.map(l => (
                <tr key={l.key} style={{ textAlign: 'right' }}>
                  <td style={{ textAlign: 'left', padding: '4px 0' }}>{l.label}</td>
                  <td style={{ padding: '4px 0' }}>{oereToKr(l.ekskl_oere)} kr.</td>
                  {momspligtig && <td style={{ padding: '4px 0' }}>{oereToKr(l.moms_oere)} kr.</td>}
                  <td style={{ padding: '4px 0' }}>{oereToKr(l.inkl_oere)} kr.</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ textAlign: 'right', borderTop: '1px solid #e5e7eb', fontWeight: 600 }}>
                <td style={{ textAlign: 'left', padding: '6px 0' }}>I alt</td>
                <td style={{ padding: '6px 0' }}>{oereToKr(indflytningsopgoerelse.totals.ekskl_oere)} kr.</td>
                {momspligtig && <td style={{ padding: '6px 0' }}>{oereToKr(indflytningsopgoerelse.totals.moms_oere)} kr.</td>}
                <td style={{ padding: '6px 0' }}>{oereToKr(indflytningsopgoerelse.totals.inkl_oere)} kr.</td>
              </tr>
            </tfoot>
          </table>
        </SectionBlock>
      )}
    </div>,

    // 4 — Regulering
    <div key="r">
      <SectionBlock icon={IcoChart} title="Regulering af leje" subtitle="Hvordan reguleres lejen over tid?">
        <RadioPillList options={REG_OPTIONS} value={reguleringType} onChange={v => setReguleringType((v || 'ingen') as ReguleringType)} />
        {reguleringType === 'nettopris' && (
          <div style={{ marginTop: 14 }}>
            {/* D1 — Nettoprisindeksregulering: a single Indeksmåned + a "mere end
                ét år tilbage?" flag (no index table). */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FieldRow label="Indeksmåned" required help="Vælg hvilken måned indekstallet skal tage udgangspunkt i.">
                <select style={inputStyle} value={reguleringIndeksMaaned} onChange={e => setReguleringIndeksMaaned(e.target.value)}>
                  {REG_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Skal indeksmåneden være mere end ét år tilbage?" help="Vær opmærksom på, at dette ikke er standardprocedure.">
                <Toggle on={reguleringIndeksMereEndAar} onToggle={() => setReguleringIndeksMereEndAar(v => !v)} />
              </FieldRow>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <FieldRow label="Reguleringsmåned" required>
                <select style={inputStyle} value={reguleringMaaned} onChange={e => setReguleringMaaned(e.target.value)}>
                  {REG_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Første reguleringsår">
                <input style={inputStyle} value={reguleringFoersteAar} inputMode="numeric" placeholder="2027" onChange={e => setReguleringFoersteAar(e.target.value.replace(/[^\d]/g, '').slice(0, 4))} />
              </FieldRow>
            </div>

            {/* Grænser for regulering (caps) — D4 shared Type-aware block. */}
            {capsSection}

            {varslingSection}

            {/* Regulering af depositum og forudbetalt leje */}
            <SubSectionDivider title="Regulering af depositum og forudbetalt leje" />
            <FieldRow label="Skal depositum reguleres?"><Toggle on={regulerDepositum} onToggle={() => setRegulerDepositum(v => !v)} /></FieldRow>
            <FieldRow label="Skal forudbetalt leje reguleres?"><Toggle on={regulerForudbetalt} onToggle={() => setRegulerForudbetalt(v => !v)} /></FieldRow>
          </div>
        )}
        {reguleringType === 'indeks' && (
          <div style={{ marginTop: 14 }}>
            {/* Reguleringsmåned + første år */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FieldRow label="Reguleringsmåned" required>
                <select style={inputStyle} value={reguleringMaaned} onChange={e => setReguleringMaaned(e.target.value)}>
                  {REG_MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Første reguleringsår">
                <input style={inputStyle} value={reguleringFoersteAar} inputMode="numeric" placeholder="2027" onChange={e => setReguleringFoersteAar(e.target.value.replace(/[^\d]/g, '').slice(0, 4))} />
              </FieldRow>
            </div>

            {/* Repeatable index rows */}
            <SubSectionDivider title="Indeks" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {indexRows.map((r, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 0.8fr auto', gap: 8, alignItems: 'center' }}>
                  <select style={inputStyle} value={r.indekstype} onChange={e => setIndexRows(rows => rows.map((x, j) => j === i ? { ...x, indekstype: e.target.value } : x))}>
                    <option value="">Vælg type</option>
                    {INDEKS_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <input style={inputStyle} placeholder="Indeksmåned/-kvartal" value={r.indeks_basis_period} onChange={e => setIndexRows(rows => rows.map((x, j) => j === i ? { ...x, indeks_basis_period: e.target.value } : x))} />
                  <input style={inputStyle} placeholder="%" inputMode="decimal" value={r.procent} onChange={e => setIndexRows(rows => rows.map((x, j) => j === i ? { ...x, procent: num(e.target.value) } : x))} />
                  <button type="button" onClick={() => setIndexRows(rows => rows.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }} aria-label="Fjern indeks">×</button>
                </div>
              ))}
              <button type="button" onClick={() => setIndexRows(rows => [...rows, { indekstype: '', indeks_basis_period: '', procent: '100' }])} style={{ alignSelf: 'flex-start', fontSize: 13, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Tilføj indeks</button>
            </div>

            {/* Grænser for regulering (caps) — D4 shared Type-aware block. */}
            {capsSection}

            {varslingSection}

            {/* Regulering af depositum og forudbetalt leje */}
            <SubSectionDivider title="Regulering af depositum og forudbetalt leje" />
            <FieldRow label="Skal depositum reguleres?"><Toggle on={regulerDepositum} onToggle={() => setRegulerDepositum(v => !v)} /></FieldRow>
            <FieldRow label="Skal forudbetalt leje reguleres?"><Toggle on={regulerForudbetalt} onToggle={() => setRegulerForudbetalt(v => !v)} /></FieldRow>
          </div>
        )}
        {reguleringType === 'fast_pct' && (
          <div style={{ marginTop: 14 }}>
            {/* D2 — Type selector: Procentvis (%) or Fast beløb (kr./år). */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FieldRow label="Type" required>
                <select style={inputStyle} value={reguleringFastType} onChange={e => setReguleringFastType(e.target.value as 'procent' | 'beloeb')}>
                  <option value="procent">Procentvis</option>
                  <option value="beloeb">Fast beløb</option>
                </select>
              </FieldRow>
              {reguleringFastType === 'procent' ? (
                <FieldRow label="Fast %/år"><input style={inputStyle} value={reguleringPct} inputMode="decimal" onChange={e => setReguleringPct(num(e.target.value))} /></FieldRow>
              ) : (
                <FieldRow label="Fast beløb (kr./år)"><input style={inputStyle} value={reguleringFastBeloeb} inputMode="decimal" placeholder="0" onChange={e => setReguleringFastBeloeb(num(e.target.value))} /></FieldRow>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <FieldRow label="Første regulering"><input type="date" style={inputStyle} value={reguleringFirstDate} onChange={e => setReguleringFirstDate(e.target.value)} /></FieldRow>
            </div>
            {varslingSection}
          </div>
        )}
        {reguleringType === 'markedsleje' && (
          <div style={{ marginTop: 14 }}>
            <FieldRow label="Markedsleje (mål, årlig kr.)" help="Du indtaster markedslejen — vi beregner den ikke. Indfases over 4 år (§ 13).">
              <input style={inputStyle} value={markedslejeDkk} inputMode="decimal" onChange={e => setMarkedslejeDkk(num(e.target.value))} />
            </FieldRow>
          </div>
        )}
        {reguleringType === 'trappe' && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* D3 — Type selector: Procentvis (År→%) or Fastpris (År→kr.). */}
            <FieldRow label="Type" required>
              <select style={inputStyle} value={trappeType} onChange={e => setTrappeType(e.target.value as 'procent' | 'beloeb')}>
                <option value="procent">Procentvis</option>
                <option value="beloeb">Fastpris</option>
              </select>
            </FieldRow>
            {trappe.map((s, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <input type="date" style={inputStyle} value={s.ikrafttraeden} onChange={e => setTrappe(t => t.map((x, j) => j === i ? { ...x, ikrafttraeden: e.target.value } : x))} />
                {trappeType === 'procent' ? (
                  <input style={inputStyle} placeholder="Procent (%)" value={s.procent != null ? String(s.procent) : ''} inputMode="decimal" onChange={e => setTrappe(t => t.map((x, j) => j === i ? { ...x, procent: e.target.value ? parseFloat(num(e.target.value)) : undefined } : x))} />
                ) : (
                  <input style={inputStyle} placeholder="Årlig leje (kr.)" value={s.aarlig_leje_oere ? String(s.aarlig_leje_oere / 100) : ''} inputMode="decimal" onChange={e => setTrappe(t => t.map((x, j) => j === i ? { ...x, aarlig_leje_oere: toOere(e.target.value) } : x))} />
                )}
              </div>
            ))}
            <button type="button" onClick={() => setTrappe(t => [...t, { ikrafttraeden: '', aarlig_leje_oere: 0 }])} style={{ alignSelf: 'flex-start', fontSize: 13, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Tilføj ekstra trin</button>
            {varslingSection}
          </div>
        )}
      </SectionBlock>
    </div>,

    // 5 — Vilkår (rendered FULL-BLEED via WizardChrome's fullBleedSection={5},
    // like the residential §11 step: the library panel sits flush at the right
    // viewport edge with its own scroll; banner + drift og skatter live in the
    // left column).
    <ErhvervSectionVilkaar
      key="v"
      items={vilkaarItems}
      library={library}
      sectionNo={vilkaarNo}
      onChange={setVilkaarItems}
      onSaveTemplate={(title, content) => {
        apiFetch('/api/v1/vilkaar', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ scope: 'company', title, content, category: 'erhverv' }) })
          .then(() => apiFetch<LibraryClause[]>('/api/v1/vilkaar/erhverv-library').then(setLibrary))
          .catch(() => {})
      }}
      banner={
        <>
          <div style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', color: '#3730a3', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Vilkår for erhvervslejemålet</div>
            <div style={{ fontSize: 12, lineHeight: 1.6, color: '#4338ca' }}>
              Vælg standardvilkår fra biblioteket til højre, eller skriv dine egne. Punkterne nummereres {vilkaarNo}.1, {vilkaarNo}.2 … i kontrakten.
            </div>
          </div>
          {clausesSyncBlocked && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: '#92400e', lineHeight: 1.6 }}>
              Vilkårene kunne ikke hentes fra kladden. Ændringer i vilkår gemmes ikke, før siden er genindlæst og kladden hentet korrekt — genindlæs siden for at prøve igen.
            </div>
          )}
        </>
      }
      footer={
        <div style={{ marginTop: 28, borderTop: '1px solid #f0f0f0', paddingTop: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 2 }}>Drift og skatter</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>Fri allokering af driftsudgifter og ejendomsskatter (valgfri)</div>
          <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} value={driftSkatter} onChange={e => setDriftSkatter(e.target.value)} />
        </div>
      }
    />,

    // 6 — Parter
    <div key="p">
      <SectionBlock icon={IcoUser} title="Parter" subtitle="Udlejer og lejer">
        <FieldRow label="Udlejer"><div style={{ fontSize: 14, color: '#111827', padding: '4px 0' }}>{company?.name ?? '—'}{company?.cvr ? ` · CVR ${company.cvr}` : ''}</div></FieldRow>
        <FieldRow label="Lejer" required>
          <select style={inputStyle} value={renterId} onChange={e => setRenterId(e.target.value)}>
            <option value="">Vælg lejer</option>
            {renters.map(r => <option key={r.id} value={r.id}>{r.name}{r.party_kind === 'selskab' ? ` (Selskab${r.cvr_nr ? ` · CVR ${r.cvr_nr}` : ''})` : ''}</option>)}
          </select>
        </FieldRow>
        {selectedRenter && (
          <>
            <SubSectionDivider title="Adresse" />
            <FieldRow label="Indtast lejers adresse manuelt?" help="Slå til for at indtaste adressen i felterne nedenfor i stedet for adresseopslag."><Toggle on={manualAddress} onToggle={() => setManualAddress(v => !v)} /></FieldRow>
            {manualAddress && (
              <>
                <FieldRow label="Vej og nr."><input style={inputStyle} value={addressStreet} placeholder="F.eks. Silkeborgvej 14A, st." onChange={e => setAddressStreet(e.target.value)} /></FieldRow>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12 }}>
                  <FieldRow label="Postnr."><input style={inputStyle} value={addressPostnr} inputMode="numeric" placeholder="7400" onChange={e => setAddressPostnr(e.target.value)} /></FieldRow>
                  <FieldRow label="By"><input style={inputStyle} value={addressBy} placeholder="Herning" onChange={e => setAddressBy(e.target.value)} /></FieldRow>
                  <FieldRow label="Land"><input style={inputStyle} value={addressLand} placeholder="Danmark" onChange={e => setAddressLand(e.target.value)} /></FieldRow>
                </div>
              </>
            )}
            <SubSectionDivider title="Kommunikation" />
            <FieldRow label="Har lejer ingen e-mail adresse?"><Toggle on={noEmail} onToggle={() => setNoEmail(v => !v)} /></FieldRow>
            {noEmail ? (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                  Vær opmærksom på, at hvis du ikke indtaster en e-mailadresse, kan Boligflow ikke sende e-mail til lejeren. Lejers e-mail adresse overføres over til dine tredjepartsintegrationer. Vi anbefaler at du indtaster en e-mail adresse.
                </div>
              </div>
            ) : (
              <FieldRow label="E-mail"><div style={{ fontSize: 14, color: '#111827', padding: '4px 0' }}>{selectedRenter.email || '—'}</div></FieldRow>
            )}
            <FieldRow label="Foretrækker lejer brev som kommunikationsmetode?" help="Boligflow sender som standard meddelelser på brev til lejer."><Toggle on={prefersLetter} onToggle={() => setPrefersLetter(v => !v)} /></FieldRow>
            {selectedRenter.party_kind === 'selskab' && (
              <>
                <SubSectionDivider title="E-faktura" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <FieldRow label="EAN-nummer" help="13-cifret EAN/GLN til e-faktura, hvis selskabet modtager fakturaer via EAN."><input style={inputStyle} value={eanNumber} inputMode="numeric" placeholder="5790000123456" onChange={e => setEanNumber(e.target.value.replace(/[^\d]/g, '').slice(0, 13))} /></FieldRow>
                  <FieldRow label="Faktura-e-mail" help="E-mailadresse fakturaer sendes til, hvis forskellig fra lejers e-mail."><input style={inputStyle} value={invoiceEmail} placeholder="faktura@selskab.dk" onChange={e => setInvoiceEmail(e.target.value)} /></FieldRow>
                </div>
              </>
            )}
            {selectedRenter.party_kind !== 'selskab' && (
              <>
                <SubSectionDivider title="Betaling" />
                <FieldRow label="Mangler lejer dansk CPR-nummer?" help="Lejere uden dansk CPR kan ikke benytte Betalingsservice og modtager opkrævninger med SWIFT/IBAN."><Toggle on={noCpr} onToggle={() => setNoCpr(v => !v)} /></FieldRow>
                {noCpr && (
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                    <FieldRow label="IBAN"><input style={inputStyle} value={iban} placeholder="DK50 0040 0440 1162 43" onChange={e => setIban(e.target.value)} /></FieldRow>
                    <FieldRow label="SWIFT/BIC"><input style={inputStyle} value={swift} placeholder="DABADKKK" onChange={e => setSwift(e.target.value)} /></FieldRow>
                  </div>
                )}
              </>
            )}
          </>
        )}
        <SubSectionDivider title="Medlejere og kautionister" />
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 10px' }}>
          Tilføj evt. medlejere eller kautionister. De medtages i § 1 Parterne og i underskriftsoversigten.
        </p>
        {extraParties.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {extraParties.map((ep, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr auto', gap: 8, alignItems: 'center' }}>
                <select style={inputStyle} value={ep.renter_id} onChange={e => setExtraParties(rows => rows.map((x, j) => j === i ? { ...x, renter_id: e.target.value } : x))}>
                  <option value="">Vælg lejer</option>
                  {renters.filter(r => r.id !== renterId).map(r => <option key={r.id} value={r.id}>{r.name}{r.party_kind === 'selskab' ? ` (Selskab${r.cvr_nr ? ` · CVR ${r.cvr_nr}` : ''})` : ''}</option>)}
                </select>
                <select style={inputStyle} value={ep.role} onChange={e => setExtraParties(rows => rows.map((x, j) => j === i ? { ...x, role: e.target.value as 'co_tenant' | 'guarantor' } : x))}>
                  <option value="co_tenant">Medlejer</option>
                  <option value="guarantor">Kautionist</option>
                </select>
                <button type="button" onClick={() => setExtraParties(rows => rows.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }} aria-label="Fjern part">×</button>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={() => setExtraParties(rows => [...rows, { renter_id: '', role: 'co_tenant' }])} style={{ alignSelf: 'flex-start', fontSize: 13, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: extraParties.length > 0 ? 10 : 0 }}>+ Tilføj medlejer eller kautionist</button>
      </SectionBlock>
    </div>,

    // 7 — Generering (A4-partial): paragraph include/exclude builder
    <div key="gen">
      <SectionBlock icon={IcoDoc} title="Generering af lejekontrakt" subtitle="Vælg hvilke paragraffer der medtages, og tilføj egne">
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
            Generering af erhvervslejekontrakter er nyt. Verificer lejekontrakten før afsendelse.
          </div>
        </div>
        <SubSectionDivider title="Paragraffer" />
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
          Slå de paragraffer fra, der ikke skal med i kontrakten. <b>Parterne og det lejede</b>, <b>Leje</b> samt lejeforholdets begyndelse og ophør er påkrævet og kan ikke fravælges.
        </p>
        {GEN_SECTIONS.filter(s => genSectionApplies(s.key)).length === 0 ? (
          <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Ingen valgfrie paragraffer at fravælge endnu — udfyld de tidligere trin.</p>
        ) : (
          GEN_SECTIONS.filter(s => genSectionApplies(s.key)).map(s => (
            <FieldRow key={s.key} label={s.label} help={s.hint}>
              <Toggle on={!excludedSections.has(s.key)} onToggle={() => toggleExcluded(s.key)} />
            </FieldRow>
          ))
        )}
        <SubSectionDivider title="Selvskrevne paragraffer" />
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
          Skriv egne paragraffer, der lægges ind i lejekontrakten før underskrifterne. Fravigelser og tilføjelser til standardkontrakten hører under Særlige vilkår.
        </p>
        {extraParagraphs.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
            {extraParagraphs.map((p, i) => (
              <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <input style={inputStyle} value={p.title} placeholder="Overskrift på paragraf" onChange={e => setExtraParagraphs(rows => rows.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} />
                  <button type="button" onClick={() => setExtraParagraphs(rows => rows.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }} aria-label="Fjern paragraf">×</button>
                </div>
                <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={p.content} placeholder="Indhold i paragraffen" onChange={e => setExtraParagraphs(rows => rows.map((x, j) => j === i ? { ...x, content: e.target.value } : x))} />
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={() => setExtraParagraphs(rows => [...rows, { title: '', content: '' }])} style={{ alignSelf: 'flex-start', fontSize: 13, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Tilføj ekstra paragraf</button>
      </SectionBlock>
    </div>,

    // 8 — Oversigt (A4-partial): read-only recap of every prior step + files
    <div key="ov">
      <SectionBlock icon={IcoCheck} title="Oversigt" subtitle="Gennemgå de indtastede oplysninger før underskrift">
        <OversigtRow label="Lejemål" value={lejemaal || lease.unit_no} />
        <OversigtRow label="Anvendelse og formål" value={formaal} />
        <OversigtRow label="Begyndelsesdato" value={startDate} />
        {tidsbegraenset && <OversigtRow label="Ophørsdato" value={endDate} />}
        <OversigtRow label="Momspligtig" value={momspligtig ? 'Ja' : 'Nej'} />

        <SubSectionDivider title="Leje" />
        <OversigtRow label="Månedlig leje" value={totalLejeOere > 0 ? `${oereToKr(totalLejeOere)} kr.` : '—'} />
        <OversigtRow label="Opkrævningsinterval" value={OPKRAEVNINGSINTERVAL_OPTIONS.find(o => o.value === opkraevningsinterval)?.label} />
        {lejeLines.filter(l => toOere(l.belob_dkk) > 0).map((l, i) => (
          <OversigtRow key={i} label={`· ${l.tekst || (l.kategori === 'bidrag' ? 'Bidrag' : 'Leje')}`} value={`${oereToKr(toOere(l.belob_dkk))} kr.`} />
        ))}

        {hasForbrug && <>
          <SubSectionDivider title="Forbrug" />
          {FORBRUG_TYPES.filter(({ key }) => forbrugTyper[key].afregning).map(({ key, label }) => (
            <OversigtRow key={key} label={label[0].toUpperCase() + label.slice(1)} value={AFREGNING_OPTIONS.find(o => o.value === forbrugTyper[key].afregning)?.label} />
          ))}
          {internetBidrag && <OversigtRow label="Internet" value="Lejer betaler bidrag" />}
          {tvBidrag && <OversigtRow label="TV" value="Lejer betaler bidrag" />}
        </>}

        <SubSectionDivider title="Betalinger" />
        <OversigtRow label="Depositum" value={toOere(depositDkk) > 0 ? `${oereToKr(toOere(depositDkk))} kr.` : '—'} />
        <OversigtRow label="Forudbetalt leje" value={toOere(prepaidDkk) > 0 ? `${oereToKr(toOere(prepaidDkk))} kr.` : '—'} />
        {bankgaranti && <OversigtRow label="Bankgaranti" value={bgModtaget ? 'Modtaget' : 'Ikke modtaget'} />}
        {betalingsserviceTilmeld && <OversigtRow label="Betalingsservice" value="Tilmeldes (oprettes hos Nets)" />}
        {indflytningsopkraevning && <OversigtRow label="Indflytningsopkrævning" value="Ja" />}

        {reguleringType !== 'ingen' && <>
          <SubSectionDivider title="Regulering" />
          <OversigtRow label="Type" value={REG_OPTIONS.find(o => o.value === reguleringType)?.label} />
          {sendVarsling && <OversigtRow label="Varslingsskrivelse" value="Ja" />}
          {sendVarsling && <OversigtRow label="Automatisk varsling" value={sendVarslingAuto ? 'Ja' : 'Nej'} />}
        </>}

        {(driftSkatter.trim() || vilkaarItems.length > 0) && <>
          <SubSectionDivider title="Vilkår" />
          {driftSkatter.trim() && <OversigtRow label="Drift og skatter" value={driftSkatter} />}
          {vilkaarItems.length > 0 && <OversigtRow label="Vilkårspunkter" value={String(vilkaarItems.length)} />}
        </>}

        <SubSectionDivider title="Parter" />
        <OversigtRow label="Udlejer" value={company?.name} />
        <OversigtRow label="Lejer" value={selectedRenter?.name} />
        {selectedRenter && !noEmail && <OversigtRow label="E-mail" value={selectedRenter.email} />}
        {selectedRenter && <OversigtRow label="Telefon" value={selectedRenter.phone || '—'} />}
        {extraParties.filter(ep => ep.renter_id).map((ep, i) => (
          <OversigtRow key={i} label={ep.role === 'guarantor' ? 'Kautionist' : 'Medlejer'} value={renters.find(r => r.id === ep.renter_id)?.name} />
        ))}

        <SubSectionDivider title="Filer til underskrift" />
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 8px' }}>Følgende filer vil blive sendt til underskrift:</p>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: '#111827' }}>
          <li>Lejekontrakt.pdf</li>
          {indflytningsopkraevning && <li>Indflytningsopkrævning.pdf</li>}
        </ul>
        {/* Step F — Idura MitID signing. Before send: the action button. After
            send: the read-only sent-state with each party's link + status. */}
        <SubSectionDivider title="Underskrift med MitID" />
        {sentResult ? (
          <div>
            <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#065f46' }}>Sendt til underskrift</div>
              <div style={{ fontSize: 12, color: '#047857', lineHeight: 1.6, marginTop: 2 }}>
                {sentResult.is_stub
                  ? 'Underskriftsordren er oprettet (teststub — ingen rigtig MitID-signering). Hver part har fået et signeringslink.'
                  : 'Hver part har modtaget en e-mail med sit personlige MitID-signeringslink. Status opdateres automatisk, efterhånden som parterne underskriver.'}
              </div>
            </div>
            {sentResult.signatories.map(p => {
              const signed = isSigned(p.signatory_id)
              return (
                <div key={p.signatory_id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div>
                    <div style={{ fontSize: 14, color: '#111827' }}>{partyLabel(p.role, p.renter_id)}</div>
                    <a href={p.href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: BLUE, textDecoration: 'none', wordBreak: 'break-all' }}>Åbn signeringslink</a>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(p.href)}
                      style={{ marginLeft: 10, fontSize: 11, color: '#6b7280', background: 'none', border: '1px solid #e5e7eb', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}
                    >
                      Kopiér link
                    </button>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: signed ? '#065f46' : '#92400e', background: signed ? '#ecfdf5' : '#fffbeb', border: `1px solid ${signed ? '#a7f3d0' : '#fde68a'}`, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                    {signed ? 'Underskrevet' : 'Afventer'}
                  </span>
                </div>
              )
            })}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => draftId && onDone(draftId)} style={{ fontSize: 13, fontWeight: 600, color: 'white', background: BLUE, border: 'none', borderRadius: 7, padding: '9px 22px', cursor: 'pointer' }}>
                Færdig
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px', lineHeight: 1.6 }}>
              Send kontrakten til digital underskrift med MitID. Udlejer og hver lejer modtager et personligt signeringslink.
            </p>
            {sendError && (
              <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>{sendError}</div>
            )}
            <button onClick={handleSend} disabled={busy || errors.length > 0} style={{ fontSize: 13, fontWeight: 600, color: 'white', background: (busy || errors.length > 0) ? '#d1d5db' : BLUE, border: 'none', borderRadius: 7, padding: '9px 22px', cursor: (busy || errors.length > 0) ? 'not-allowed' : 'pointer' }}>
              {busy ? 'Sender…' : 'Send til underskrift med MitID'}
            </button>
          </div>
        )}
      </SectionBlock>
    </div>,

    // 9 — Gennemse & send
    <div key="s">
      <SectionBlock icon={IcoCheck} title="Gennemse & send" subtitle="Tjek kontrakten og send til underskrift">
        {sentResult ? (
          <p style={{ fontSize: 13, color: '#065f46', margin: 0 }}>Kontrakten er sendt til underskrift. Se status under <b>Oversigt</b>.</p>
        ) : (
          <>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>Klik <b>Forhåndsvis</b> for at se kontrakten, eller <b>Send til underskrift med MitID</b> for at oprette underskriftsordren.</p>
            {sendError && (
              <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>{sendError}</div>
            )}
            <button onClick={handleSend} disabled={busy || errors.length > 0} style={{ fontSize: 13, fontWeight: 600, color: 'white', background: (busy || errors.length > 0) ? '#d1d5db' : BLUE, border: 'none', borderRadius: 7, padding: '9px 22px', cursor: (busy || errors.length > 0) ? 'not-allowed' : 'pointer' }}>
              {busy ? 'Sender…' : 'Send til underskrift med MitID'}
            </button>
          </>
        )}
      </SectionBlock>
    </div>,
  ]

  return (
    <WizardChrome
      navSteps={NAV_STEPS}
      sections={sections}
      fullBleedSection={5}
      currentSection={step}
      stepStatus={stepStatus}
      onSectionChange={goStep}
      view={view}
      setView={setView}
      onPreview={handlePreview}
      saving={busy}
      onNext={() => goStep(Math.min(NAV_STEPS.length - 1, step + 1))}
      onBack={() => goStep(Math.max(0, step - 1))}
      onSaveDraft={handleSaveDraft}
      onSaveDraftAndClose={handleSaveDraftAndClose}
      onDeleteDraft={handleDeleteDraft}
      validationErrors={errors}
      onSend={handleSend}
      renderPreview={(zoom) => (
        <ErhvervPreviewPanel body={previewBody()} fetchKey={fetchKey} zoom={zoom} onPdfLoad={setPdfUrl} onNumPages={setThumbPages} />
      )}
      pdfUrl={pdfUrl}
      thumbPages={thumbPages}
      downloadName="erhvervslejekontrakt-udkast.pdf"
    />
  )
}
